<template>
  <div>
    <b-row>
      <b-col cols="12">
        <b-card class="asset-overview-card p-1">
          <b-row class="no-gutters">
            <!-- Informações Principais do Ativo -->
            <b-col md="7" class="info-section pr-1">
              <!-- Header com Avatar e Título -->
              <div class="asset-header mb-2">
                <div class="avatar-container-small">
                  <div class="asset-avatar-small"
                    :class="`asset-status-${assetSummary.lastWorkStatus || 'noconnection'}`">
                    <img :src="resolveIcon(assetInfo.assetType, assetInfo.iconUrl)"
                      :alt="assetInfo.modelIdName || 'Asset'" @error="handleImageError" @load="handleImageLoad" />
                  </div>
                  <div class="status-indicator-small"
                    :class="`status-${assetSummary.lastWorkStatus || 'noconnection'}`">
                  </div>
                </div>

                <div class="asset-title-section">
                  <div class="d-flex align-items-center flex-wrap mb-1">
                    <h4 class="asset-model mb-0 mr-2">
                      {{ assetInfo.modelIdName || 'N/A' }}
                      <span v-if="assetInfo.modelYear" class="text-muted font-weight-normal">({{ assetInfo.modelYear
                        }})</span>
                    </h4>
                    <b-badge :variant="getStatusVariant(assetSummary.lastWorkStatus)" class="status-badge-header">
                      {{ $t(assetSummary.lastWorkStatus || 'noconnection') }}
                    </b-badge>
                  </div>

                  <div class="asset-identifier">
                    <feather-icon icon="HashIcon" size="14" class="mr-1 text-primary" />
                    <strong v-if="user && user.role === 'TRCKOWN'" class="text-primary cursor-pointer" role="button"
                      tabindex="0" @click="toAssetRegister" @keydown.enter.prevent="toAssetRegister"
                      @keydown.space.prevent="toAssetRegister">{{ assetInfo.plate || assetInfo.chassisNumber || 'N/A'
                      }}</strong>
                    <strong v-else class="text-primary">{{ assetInfo.plate || assetInfo.chassisNumber || 'N/A'
                      }}</strong>
                    <b-button @click="toAssetPreview()" class="ml-sm-1 py-0 px-1 small" variant="outline-primary"
                      size="sm" v-b-tooltip.hover.top :title="$t('Classic View tooltip')">{{ $t('Classic View')
                      }}</b-button>
                  </div>
                </div>
              </div>

              <div class="asset-main-info">
                <div class="asset-details-grid">
                  <div class="detail-item">
                    <div class="detail-label">
                      <feather-icon icon="UserIcon" size="14" class="mr-1" />
                      {{ $t('Customer') }}
                    </div>
                    <div class="detail-value">{{ assetInfo.ownerName || 'N/A' }}</div>
                  </div>

                  <div v-if="assetInfo.dealerName" class="detail-item">
                    <div class="detail-label">
                      <feather-icon icon="StoreIcon" size="14" class="mr-1" />
                      {{ $t('Dealer') }}
                    </div>
                    <div class="detail-value">{{ assetInfo.dealerName }}</div>
                  </div>

                  <div v-if="deviceModemIdentifier" class="detail-item">
                    <div class="detail-label">
                      <feather-icon icon="RadioIcon" size="14" class="mr-1" />
                      {{ $t('Modem Identifier') }}
                    </div>
                    <div class="detail-value">{{ deviceModemIdentifier }}</div>
                  </div>
                </div>

                <div class="asset-metrics mt-2">
                  <div v-if="odometerHourmeter.odometer" class="metric-item">
                    <feather-icon icon="NavigationIcon" size="16" class="text-primary mr-2" />
                    <div>
                      <div class="metric-label">{{ $t('Odometer') }}</div>
                      <div class="metric-value">{{ odometerHourmeter.odometer }} <small>Km</small></div>
                      <div v-if="odometerHourmeter.odometerTimestamp" class="metric-timestamp">
                        {{ odometerHourmeter.odometerTimestamp }}
                      </div>
                    </div>
                  </div>

                  <div v-if="odometerHourmeter.hourmeter" class="metric-item">
                    <feather-icon icon="ClockIcon" size="16" class="text-info mr-2" />
                    <div>
                      <div class="metric-label">{{ $t('Hour Meter') }}</div>
                      <div class="metric-value">{{ odometerHourmeter.hourmeter }} <small>h</small></div>
                      <div v-if="odometerHourmeter.hourmeterTimestamp" class="metric-timestamp">
                        {{ odometerHourmeter.hourmeterTimestamp }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>

            <!-- Seções de Falhas e Manutenção -->
            <b-col md="5" class="alerts-section pl-1">
              <div class="alerts-content-wrapper">
                <!-- Seção de Manutenção - Sempre no topo quando presente -->
                <div class="maintenance-section"
                  :class="{ 'maintenance-top': !failuresData.recentFailures || failuresData.recentFailures.length === 0 }">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0 font-weight-bold maintenance-title">
                      <feather-icon icon="ToolIcon" size="14" class="mr-1" />
                      {{ $t('Next Maintenance') }}
                    </h6>
                  </div>
                  <div v-if="maintenanceData.nextMaintenance" class="maintenance-content">
                    <div class="maintenance-info-compact">
                      <div class="maintenance-header-row">
                        <div class="maintenance-info-content">
                          <div class="maintenance-plan-row">
                            <span class="maintenance-label">{{ $t('Plan') }}:</span>
                            <span v-if="maintenanceData.nextMaintenance.planName" class="maintenance-plan-name"
                              v-b-tooltip.hover :title="maintenanceData.nextMaintenance.planName">
                              {{ maintenanceData.nextMaintenance.planName }}
                            </span>
                            <span v-else class="maintenance-plan-name text-muted">-</span>
                          </div>
                          <div class="maintenance-step-row">
                            <span class="maintenance-label">{{ $t('Step') }}:</span>
                            <span class="maintenance-step-name" v-b-tooltip.hover
                              :title="maintenanceData.nextMaintenance.name">
                              {{ maintenanceData.nextMaintenance.name }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="maintenance-metrics-row">
                        <div class="maintenance-metric">
                          <feather-icon icon="TargetIcon" size="10" class="mr-1" />
                          <small class="text-muted">
                            <strong>{{ maintenanceData.nextMaintenance.scheduledValue }}</strong>
                            {{ maintenanceData.nextMaintenance.isHourmeter ? 'h' : 'Km' }}
                          </small>
                        </div>
                        <div class="maintenance-metric">
                          <feather-icon
                            :icon="maintenanceData.nextMaintenance.remaining > 0 ? 'ClockIcon' : 'AlertCircleIcon'"
                            size="10" class="mr-1"
                            :class="maintenanceData.nextMaintenance.remaining > 0 ? 'text-success' : 'text-danger'" />
                          <small class="font-weight-medium"
                            :class="maintenanceData.nextMaintenance.remaining > 0 ? 'text-success' : 'text-danger'">
                            {{ maintenanceData.nextMaintenance.remaining > 0 ? $t('Remaining:') : $t('Overdue:') }}
                            <strong>{{ Math.abs(maintenanceData.nextMaintenance.remaining) }}</strong>
                            {{ maintenanceData.nextMaintenance.isHourmeter ? 'h' : 'Km' }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="maintenance-empty">
                    <div class="maintenance-empty-content">
                      <div class="maintenance-empty-icon">
                        <feather-icon icon="ToolIcon" size="16" />
                      </div>
                      <div class="maintenance-empty-text">
                        <div class="maintenance-empty-title">
                          {{ !maintenanceData.hasMaintenancePlan ? $t('No maintenance plan registered') : $t('No maintenance scheduled') }}
                        </div>
                        <div class="maintenance-empty-description">
                          {{ !maintenanceData.hasMaintenancePlan
                            ? $t('No maintenance plan has been registered for this asset')
                            : $t('No upcoming maintenance has been scheduled') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Divisor - Apenas quando há falhas -->
                <hr v-if="failuresData.recentFailures && failuresData.recentFailures.length > 0"
                  class="alerts-divider" />

                <!-- Seção de Falhas -->
                <div v-if="failuresData.recentFailures && failuresData.recentFailures.length > 0"
                  class="failures-section">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0 font-weight-bold failures-title">
                      <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
                      {{ $t('Last Failures') }}
                    </h6>
                    <small class="text-muted" style="font-size: 0.6875rem;">7 {{ $t('days') }} </small>
                  </div>

                  <!-- Lista de falhas -->
                  <div class="failures-list">
                    <div v-for="(failure, index) in failuresData.recentFailures" :key="index" class="failure-card"
                      :class="{
                        'failure-card-warning': failure.failureType === 2,
                        'failure-card-danger': failure.failureType === 3
                      }">
                      <div class="failure-card-header">
                        <div class="failure-header-content">
                          <div class="failure-code-title">{{ failure.failureCode }}</div>
                          <div class="failure-date-label">
                            {{ formatFailureDate(failure.failureDate) }}
                          </div>
                          <b-badge :variant="failure.failureType === 3 ? 'danger' : 'warning'" pill
                            class="failure-type-badge">
                            {{ getFailureTypeLabel(failure.failureType) }}
                          </b-badge>
                        </div>
                      </div>
                      <div class="failure-card-body">
                        <div class="failure-description" v-b-tooltip.hover :title="failure.failureDescription">
                          {{ failure.failureDescription }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
      <b-col cols="12" md="12">
        <div class="modern-tabs-wrapper">
          <b-tabs v-model="mainAssetTabIndex" pills nav-class="modern-tabs-nav mb-0" content-class="modern-tabs-content"
            active-nav-item-class="active-tab">
            <b-tab active>
              <template #title>
                <feather-icon icon="BarChart2Icon" size="16" class="mr-1" />
                <span>{{ $t('Summary') }}</span>
              </template>
              <div class="tab-content-wrapper">
                <b-card class="map-card" :class="{ 'fullscreen-map-card': isFullscreen }">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h6 class="mb-0">
                        <span class="font-weight-bold">{{ $t('Last Position') }}:</span>
                        <span class="text-muted ml-1">
                          {{ lastPositionTime && lastPositionTime !== '' && !lastPositionTime.includes('1969') ?
                            lastPositionTime :
                            $t('Not Reported') }}
                        </span>
                      </h6>
                    </div>
                    <div class="d-flex gap-2">
                      <b-button size="sm" variant="outline-primary" @click="toggleFullscreen" v-b-tooltip.hover
                        :title="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')">
                        <feather-icon :icon="isFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'" size="16" />
                      </b-button>
                      <b-button v-if="latLong && latLong[0] && latLong[1]" size="sm" variant="info" @click="toGoogleMap"
                        v-b-tooltip.hover :title="$t('Go to Google Maps')">
                        <feather-icon icon="MapPinIcon" size="16" class="mr-1" />
                        {{ $t('Google Maps') }}
                      </b-button>
                    </div>
                  </div>
                  <l-map ref="summaryMap" class="summary-map" :zoom="zoom" :center="center" :options="mapOptions"
                    @ready="onMapReady">
                    <!-- Layers Control -->
                    <l-control-layers position="topright"></l-control-layers>

                    <!-- Tile Layers -->
                    <l-tile-layer :name="$t('Map')" :url="'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'"
                      layer-type="base" :checked="defaultLayer === 'map'"></l-tile-layer>

                    <!-- ArcGIS Híbrido (Imagem de Satélite + Labels de Ruas) -->
                    <l-tile-layer :name="$t('Satellite')" :url="arcgisSatelliteUrl" layer-type="base"
                      :checked="defaultLayer === 'satellite'"></l-tile-layer>

                    <l-tile-layer :name="$t('Show locations')" :url="arcgisLabelsUrl"
                      layer-type="overlay"></l-tile-layer>

                    <l-tile-layer :name="$t('Show street names')" :url="arcgisRoadsUrl"
                      layer-type="overlay"></l-tile-layer>

                    <!-- Marker do ativo -->
                    <l-marker v-if="latLong && latLong[0] !== 0 && latLong[1] !== 0 && imageicon" :lat-lng="latLong"
                      :key="`marker-${latLong[0]}-${latLong[1]}-${imageicon}`">
                      <l-icon :class-name="`someExtraClass asset-${assetSummary.lastWorkStatus || 'noconnection'}`"
                        :icon-size="dynamicSize" :tooltip-anchor="dynamicTooltip" :popup-anchor="dynamicPopup"
                        :icon-anchor="dynamicAnchor">
                        <img class="with-icon" :src="imageicon" @error="handleImageError" @load="handleImageLoad">
                      </l-icon>
                    </l-marker>
                  </l-map>
                </b-card>

                <!-- Gráfico de Status de Trabalho -->
                <b-card class="mt-3 status-history-card">
                  <status-card :dataWorkStatus="dataWorkStatus" :assetId="myId" :minTime="workStatusMinTime"
                    :maxTime="workStatusMaxTime" :period="workStatusPeriod"
                    v-if="dataWorkStatus && dataWorkStatus.length > 0" />
                  <b-alert v-else-if="!workStatusLoading" variant="light" show class="mb-0">
                    {{ $t('No work status history available for today') }}
                  </b-alert>
                  <div v-else class="text-center py-4">
                    <b-spinner small type="grow" variant="primary" />
                  </div>
                </b-card>
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <feather-icon icon="ActivityIcon" size="16" class="mr-1" />
                <span>{{ $t('Panel') }}</span>
              </template>
              <div class="tab-content-wrapper">
                <b-tabs pills nav-class="system-tabs-nav mb-3" content-class="system-tabs-content">
                  <!-- Aba "Todas as informações" - selecionada por padrão -->
                  <b-tab active>
                    <template #title>
                      <feather-icon icon="GridIcon" size="14" class="mr-1" />
                      <span>{{ $t('All information') }}</span>
                    </template>
                    <div class="system-panel-content">
                      <!-- Todas as informações em uma única grade, sem distinção de sistemas -->
                      <b-row v-if="hasAllPanelData">
                        <template v-for="system in availableSystems">
                          <template v-if="system.name !== 'Tires'">
                            <b-col v-for="item in getSystemData(system.name)" :key="`${system.name}-${item.label}`"
                              xl="3" lg="4" md="6" sm="12" class="mb-2">
                              <b-card class="system-info-card-compact shadow-sm h-100">
                                <div class="system-info-content-compact">
                                  <div class="system-info-icon-compact">
                                    <feather-icon :icon="item.icon" size="18" />
                                  </div>
                                  <div class="system-info-details-compact">
                                    <div class="system-info-label-compact">{{ item.label }}</div>
                                    <div class="system-info-value-compact">
                                      {{ item.value }}<span class="system-info-unit-compact">{{ item.unit }}</span>
                                    </div>
                                    <div class="system-info-timestamp-compact">
                                      <feather-icon icon="ClockIcon" size="10" class="mr-1" />
                                      {{ item.timestamp }}
                                    </div>
                                  </div>
                                </div>
                              </b-card>
                            </b-col>
                          </template>
                        </template>
                        <template v-if="tiresData.length > 0">
                          <b-col v-for="tire in tiresData" :key="`tire-${tire.index}`" xl="3" lg="4" md="6" sm="12"
                            class="mb-2">
                            <b-card class="shadow-sm h-100">
                              <div class="d-flex align-items-center">
                                <div class="tire-visual mr-2">
                                  <div class="tire-circle">
                                    <div class="tire-inner">
                                      <div class="tire-label small">{{ $t('Tire') }} {{ tire.index }}</div>
                                    </div>
                                  </div>
                                </div>
                                <div class="tire-info flex-grow-1">
                                  <div v-if="tire.pressure !== null" class="tire-metric">
                                    <div class="tire-metric-label">{{ $t('Pressure') }}</div>
                                    <div class="tire-metric-value">{{ tire.pressure }} <span
                                        class="tire-metric-unit">kPa</span></div>
                                  </div>
                                  <div v-if="tire.temperature !== null" class="tire-metric">
                                    <div class="tire-metric-label">{{ $t('Temperature') }}</div>
                                    <div class="tire-metric-value">{{ tire.temperature }} <span
                                        class="tire-metric-unit">°C</span></div>
                                  </div>
                                  <div class="tire-timestamp-compact">
                                    <feather-icon icon="ClockIcon" size="10" class="mr-1" />
                                    {{ tire.timestamp }}
                                  </div>
                                </div>
                              </div>
                            </b-card>
                          </b-col>
                        </template>
                      </b-row>
                      <div v-else class="text-center py-5">
                        <p class="text-muted">{{ $t('No data available') }}</p>
                      </div>
                    </div>
                  </b-tab>
                  <b-tab v-for="(system, index) in availableSystems" :key="system.name">
                    <template #title>
                      <feather-icon :icon="system.icon" size="14" class="mr-1" />
                      <span>{{ system.label }}</span>
                    </template>
                    <div class="system-panel-content">
                      <!-- Conteúdo para sistemas normais -->
                      <template v-if="system.name !== 'Tires'">
                        <b-row v-if="getSystemData(system.name).length > 0">
                          <b-col v-for="item in getSystemData(system.name)" :key="item.label" xl="3" lg="4" md="6"
                            sm="12" class="mb-2">
                            <b-card class="system-info-card-compact shadow-sm h-100">
                              <div class="system-info-content-compact">
                                <div class="system-info-icon-compact">
                                  <feather-icon :icon="item.icon" size="18" />
                                </div>
                                <div class="system-info-details-compact">
                                  <div class="system-info-label-compact">{{ item.label }}</div>
                                  <div class="system-info-value-compact">
                                    {{ item.value }}<span class="system-info-unit-compact">{{ item.unit }}</span>
                                  </div>
                                  <div class="system-info-timestamp-compact">
                                    <feather-icon icon="ClockIcon" size="10" class="mr-1" />
                                    {{ item.timestamp }}
                                  </div>
                                </div>
                              </div>
                            </b-card>
                          </b-col>
                        </b-row>
                        <div v-else class="text-center py-5">
                          <p class="text-muted">{{ $t('No data available') }}</p>
                        </div>
                      </template>
                      <!-- Conteúdo especial para pneus -->
                      <template v-else>
                        <div v-if="tiresData.length > 0" class="tpms-container">
                          <div class="tpms-grid-custom" :class="`tpms-${tiresData.length}`">
                            <div v-for="tire in tiresData" :key="tire.index" class="tire-card"
                              :class="`tire-pos-${tire.index}`">
                              <div class="tire-visual">
                                <div class="tire-circle">
                                  <div class="tire-inner">
                                    <div class="tire-label">{{ $t('Tire') }} {{ tire.index }}</div>
                                  </div>
                                </div>
                              </div>
                              <div class="tire-info">
                                <div v-if="tire.pressure !== null" class="tire-metric">
                                  <div class="tire-metric-label">{{ $t('Pressure') }}</div>
                                  <div class="tire-metric-value">{{ tire.pressure }} <span
                                      class="tire-metric-unit">kPa</span></div>
                                </div>
                                <div v-if="tire.temperature !== null" class="tire-metric">
                                  <div class="tire-metric-label">{{ $t('Temperature') }}</div>
                                  <div class="tire-metric-value">{{ tire.temperature }} <span
                                      class="tire-metric-unit">°C</span></div>
                                </div>
                                <div class="tire-timestamp-compact">
                                  <feather-icon icon="ClockIcon" size="10" class="mr-1" />
                                  {{ tire.timestamp }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-center py-5">
                          <p class="text-muted">{{ $t('No tire data available') }}</p>
                        </div>
                      </template>
                    </div>
                  </b-tab>
                </b-tabs>
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <feather-icon icon="TrendingUpIcon" size="16" class="mr-1" />
                <span>{{ $t('Daily Chart') }}</span>
              </template>
              <div class="tab-content-wrapper">
                <!-- Parâmetros de telemetria ainda carregando -->
                <b-row v-if="telemetryParamsLoading" class="d-flex justify-content-center py-5">
                  <b-col cols="12" class="text-center">
                    <b-spinner small type="grow" variant="primary" />
                    <p class="mt-2 text-muted">{{ $t('Loading telemetry parameters...') }}</p>
                  </b-col>
                </b-row>

                <template v-else-if="!telemetryParameters.length">
                  <b-alert variant="light" show class="mt-3 mb-0">
                    {{ $t('No telemetry parameters available for charts') }}
                  </b-alert>
                </template>

                <template v-else>
                  <!-- Seletor de Data -->
                  <b-row class="mb-3">
                    <b-col cols="12" md="4" class="mb-md-0 mb-2">
                      <label class="font-weight-bold">{{ $t('Choose the Date') }}</label>
                      <flat-pickr v-model="chartDate" class="form-control" :config="flatpickrConfig"
                        @on-change="onChartDateChange" />
                    </b-col>
                  </b-row>

                  <!-- Loading State -->
                  <b-row v-if="chartLoading" class="d-flex justify-content-center py-5">
                    <b-col cols="12" class="text-center">
                      <b-spinner small type="grow" variant="primary" />
                      <p class="mt-2 text-muted">{{ $t('Loading chart data...') }}</p>
                    </b-col>
                  </b-row>

                  <!-- Empty State -->
                  <b-alert v-else-if="!hasChartData" variant="light" show class="mt-3 mb-0">
                    {{ $t('No chart data available for the selected date') }}
                  </b-alert>

                  <!-- Gráficos -->
                  <div v-else class="charts-container">
                    <b-alert variant="warning" show class="mb-3 mt-1 d-flex align-items-center p-1">
                      <feather-icon icon="InfoIcon" size="18" class="mr-1" />
                      <span>{{ $t('To ensure accuracy, communication intervals exceeding 55 minutes interrupt the line connection in the charts below.') }}</span>
                    </b-alert>

                    <!-- RPM -->
                    <b-row v-if="timeConnectionRpm.length > 0 && hasNonZeroData(rpmHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="r/min" :title="$t('RPM')" :axis-x="timeConnectionRpm"
                            :axis-y="rpmHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('rpm', 'upper')" :lower-limit="getChartLimit('rpm', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Engine Temperature -->
                    <b-row v-if="timeConnectionEngine.length > 0 && hasNonZeroData(engineHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Engine Temperature')"
                            :axis-x="timeConnectionEngine" :axis-y="engineHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tpa', 'upper')"
                            :lower-limit="getChartLimit('tpa', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Hydraulic Oil Temperature -->
                    <b-row v-if="timeConnectionOil.length > 0 && hasNonZeroData(oilHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Hydraulic Oil Temperature')"
                            :axis-x="timeConnectionOil" :axis-y="oilHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('toh', 'upper')"
                            :lower-limit="getChartLimit('toh', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Transmission Temperature -->
                    <b-row v-if="timeConnectionTransmission.length > 0 && hasNonZeroData(transmissionHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Transmission Temperature')"
                            :axis-x="timeConnectionTransmission" :axis-y="transmissionHistory"
                            :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('tpt', 'upper')" :lower-limit="getChartLimit('tpt', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Average Fuel Consumption -->
                    <b-row v-if="timeConnectionCbc.length > 0 && hasNonZeroData(cbcHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="L/h" :title="$t('Average Fuel Consumption')"
                            :axis-x="timeConnectionCbc" :axis-y="cbcHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('cbc', 'upper')"
                            :lower-limit="getChartLimit('cbc', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Level Fuel -->
                    <b-row v-if="timeConnectionCbt.length > 0 && hasNonZeroData(cbtHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="%" :title="$t('Level Fuel')" :axis-x="timeConnectionCbt"
                            :axis-y="cbtHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('cbt', 'upper')" :lower-limit="getChartLimit('cbt', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Fuel Pressure -->
                    <b-row v-if="timeConnectionPcb.length > 0 && hasNonZeroData(pcbHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Fuel Pressure')" :axis-x="timeConnectionPcb"
                            :axis-y="pcbHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('pcb', 'upper')" :lower-limit="getChartLimit('pcb', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Engine Oil Pressure -->
                    <b-row v-if="timeConnectionPom.length > 0 && hasNonZeroData(pomHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Engine Oil Pressure')" icon="InfoIcon"
                            :axis-x="timeConnectionPom" :axis-y="pomHistory"
                            :temperature="`${getTelemetryValue('pom', '')}`" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pom', 'upper')"
                            :lower-limit="getChartLimit('pom', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Turbo Pressure -->
                    <b-row v-if="timeConnectionPtb.length > 0 && hasNonZeroData(ptbHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Turbo Pressure')" :axis-x="timeConnectionPtb"
                            :axis-y="ptbHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('ptb', 'upper')" :lower-limit="getChartLimit('ptb', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Battery Voltage (TBT) -->
                    <b-row v-if="timeConnectionTbt.length > 0 && hasNonZeroData(tbtHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="Volts" :title="$t('Battery Voltage')"
                            :axis-x="timeConnectionTbt" :axis-y="tbtHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tbt', 'upper')"
                            :lower-limit="getChartLimit('tbt', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Battery Voltage (TTB) -->
                    <b-row v-if="timeConnectionTtb.length > 0 && hasNonZeroData(ttbHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="Volts" :title="$t('Battery Voltage')"
                            :axis-x="timeConnectionTtb" :axis-y="ttbHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('ttb', 'upper')"
                            :lower-limit="getChartLimit('ttb', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Acceleration -->
                    <b-row v-if="timeConnectionAcl.length > 0 && hasNonZeroData(aclHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="G" :title="$t('Acceleration')" :axis-x="timeConnectionAcl"
                            :axis-y="aclHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('acl', 'upper')" :lower-limit="getChartLimit('acl', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Speed -->
                    <b-row v-if="timeConnectionKmh.length > 0 && hasNonZeroData(kmhHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="Km/h" :title="$t('Speed')" :axis-x="timeConnectionKmh"
                            :axis-y="kmhHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('kmh', 'upper')" :lower-limit="getChartLimit('kmh', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Engine Load -->
                    <b-row v-if="timeConnectionCgm.length > 0 && hasNonZeroData(cgmHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="%" :title="$t('Engine Load')" :axis-x="timeConnectionCgm"
                            :axis-y="cgmHistory" :use-date-time-scale="true" :use-utc-for-labels="true"
                            :upper-limit="getChartLimit('cgm', 'upper')" :lower-limit="getChartLimit('cgm', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Power Battery Charge -->
                    <b-row v-if="timeConnectionSoc.length > 0 && hasNonZeroData(socHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="%" :title="$t('Power Battery Charge')"
                            :axis-x="timeConnectionSoc" :axis-y="socHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('soc', 'upper')"
                            :lower-limit="getChartLimit('soc', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Power Battery Temperature -->
                    <b-row v-if="timeConnectionTpb.length > 0 && hasNonZeroData(tpbHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Power Battery Temperature')" icon="InfoIcon"
                            :axis-x="timeConnectionTpb" :axis-y="tpbHistory"
                            :temperature="`${getTelemetryValue('tpb', '')}`" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tpb', 'upper')"
                            :lower-limit="getChartLimit('tpb', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Engine oil temperature -->
                    <b-row v-if="timeConnectionTom.length > 0 && hasNonZeroData(tomHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Engine oil temperature')"
                            :axis-x="timeConnectionTom" :axis-y="tomHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tom', 'upper')"
                            :lower-limit="getChartLimit('tom', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Air Discharge Temperature -->
                    <b-row v-if="timeConnectionTda.length > 0 && hasNonZeroData(tdaHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Air Discharge Temperature')"
                            :axis-x="timeConnectionTda" :axis-y="tdaHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tda', 'upper')"
                            :lower-limit="getChartLimit('tda', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Air Discharge Pressure -->
                    <b-row v-if="timeConnectionPda.length > 0 && hasNonZeroData(pdaHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="psi" :title="$t('Air Discharge Pressure')"
                            :axis-x="timeConnectionPda" :axis-y="pdaHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pda', 'upper')"
                            :lower-limit="getChartLimit('pda', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Accelerator pedal position -->
                    <b-row v-if="timeConnectionPpa.length > 0 && hasNonZeroData(ppaHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="%" :title="$t('Accelerator pedal position')"
                            :axis-x="timeConnectionPpa" :axis-y="ppaHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('ppa', 'upper')"
                            :lower-limit="getChartLimit('ppa', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Engine oil level -->
                    <b-row v-if="timeConnectionNom.length > 0 && hasNonZeroData(nomHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="%" :title="$t('Engine oil level')"
                            :axis-x="timeConnectionNom" :axis-y="nomHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('nom', 'upper')"
                            :lower-limit="getChartLimit('nom', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Fuel filter differential pressure -->
                    <b-row v-if="timeConnectionDfc.length > 0 && hasNonZeroData(dfcHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Fuel filter differential pressure')"
                            :axis-x="timeConnectionDfc" :axis-y="dfcHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('dfc', 'upper')"
                            :lower-limit="getChartLimit('dfc', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Alternator current -->
                    <b-row v-if="timeConnectionCra.length > 0 && hasNonZeroData(craHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="A" :title="$t('Alternator current')"
                            :axis-x="timeConnectionCra" :axis-y="craHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('cra', 'upper')"
                            :lower-limit="getChartLimit('cra', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Front brake pressure -->
                    <b-row v-if="timeConnectionPff.length > 0 && hasNonZeroData(pffHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Front brake pressure')"
                            :axis-x="timeConnectionPff" :axis-y="pffHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pff', 'upper')"
                            :lower-limit="getChartLimit('pff', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Rear brake pressure -->
                    <b-row v-if="timeConnectionPft.length > 0 && hasNonZeroData(pftHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Rear brake pressure')"
                            :axis-x="timeConnectionPft" :axis-y="pftHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pft', 'upper')"
                            :lower-limit="getChartLimit('pft', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Brake pressure -->
                    <b-row v-if="timeConnectionPfr.length > 0 && hasNonZeroData(pfrHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Brake pressure')"
                            :axis-x="timeConnectionPfr" :axis-y="pfrHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pfr', 'upper')"
                            :lower-limit="getChartLimit('pfr', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Park brake pressure -->
                    <b-row v-if="timeConnectionPfm.length > 0 && hasNonZeroData(pfmHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Park brake pressure')"
                            :axis-x="timeConnectionPfm" :axis-y="pfmHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pfm', 'upper')"
                            :lower-limit="getChartLimit('pfm', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Steer pressure -->
                    <b-row v-if="timeConnectionPdr.length > 0 && hasNonZeroData(pdrHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="kPa" :title="$t('Steer pressure')"
                            :axis-x="timeConnectionPdr" :axis-y="pdrHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pdr', 'upper')"
                            :lower-limit="getChartLimit('pdr', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Turbo oil temperature -->
                    <b-row v-if="timeConnectionTot.length > 0 && hasNonZeroData(totHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Turbo oil temperature')"
                            :axis-x="timeConnectionTot" :axis-y="totHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tot', 'upper')"
                            :lower-limit="getChartLimit('tot', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Motor voltage -->
                    <b-row v-if="timeConnectionTmt.length > 0 && hasNonZeroData(tmtHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="V" :title="$t('Motor voltage')"
                            :axis-x="timeConnectionTmt" :axis-y="tmtHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tmt', 'upper')"
                            :lower-limit="getChartLimit('tmt', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Minimal Battery Cell Temperature -->
                    <b-row v-if="timeConnectionTc1.length > 0 && hasNonZeroData(tc1History)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Minimal Battery Cell Temperature')"
                            :axis-x="timeConnectionTc1" :axis-y="tc1History" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tc1', 'upper')"
                            :lower-limit="getChartLimit('tc1', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Maximum Battery Cell Temperature -->
                    <b-row v-if="timeConnectionTc2.length > 0 && hasNonZeroData(tc2History)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Maximum Battery Cell Temperature')"
                            :axis-x="timeConnectionTc2" :axis-y="tc2History" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tc2', 'upper')"
                            :lower-limit="getChartLimit('tc2', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Average Battery Cell Temperature -->
                    <b-row v-if="timeConnectionTc3.length > 0 && hasNonZeroData(tc3History)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Average Battery Cell Temperature')"
                            :axis-x="timeConnectionTc3" :axis-y="tc3History" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tc3', 'upper')"
                            :lower-limit="getChartLimit('tc3', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Break temperature -->
                    <b-row v-if="timeConnectionTpf.length > 0 && hasNonZeroData(tpfHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Break temperature')"
                            :axis-x="timeConnectionTpf" :axis-y="tpfHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tpf', 'upper')"
                            :lower-limit="getChartLimit('tpf', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Steer temperature -->
                    <b-row v-if="timeConnectionTpd.length > 0 && hasNonZeroData(tpdHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="ºC" :title="$t('Steer temperature')"
                            :axis-x="timeConnectionTpd" :axis-y="tpdHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('tpd', 'upper')"
                            :lower-limit="getChartLimit('tpd', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Altitude -->
                    <b-row v-if="timeConnectionAlt.length > 0 && hasNonZeroData(altHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="m" :title="$t('Altitude')"
                            :axis-x="timeConnectionAlt" :axis-y="altHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('alt', 'upper')"
                            :lower-limit="getChartLimit('alt', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Recharging -->
                    <b-row v-if="timeConnectionRcg.length > 0 && hasNonZeroData(rcgHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="" :title="$t('Recharging')"
                            :axis-x="timeConnectionRcg" :axis-y="rcgHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('rcg', 'upper')"
                            :lower-limit="getChartLimit('rcg', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Truck Bed Raised -->
                    <b-row v-if="timeConnectionBsc.length > 0 && hasNonZeroData(bscHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="" :title="$t('Truck Bed Raised')"
                            :axis-x="timeConnectionBsc" :axis-y="bscHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('bsc', 'upper')"
                            :lower-limit="getChartLimit('bsc', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Wind speed -->
                    <b-row v-if="timeConnectionVvt.length > 0 && hasNonZeroData(vvtHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="m/s" :title="$t('Wind speed')"
                            :axis-x="timeConnectionVvt" :axis-y="vvtHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('vvt', 'upper')"
                            :lower-limit="getChartLimit('vvt', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Boom Length -->
                    <b-row v-if="timeConnectionCpl.length > 0 && hasNonZeroData(cplHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="m" :title="$t('Crane Boom Length')"
                            :axis-x="timeConnectionCpl" :axis-y="cplHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('cpl', 'upper')"
                            :lower-limit="getChartLimit('cpl', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Boom Angle -->
                    <b-row v-if="timeConnectionAgl.length > 0 && hasNonZeroData(aglHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="°" :title="$t('Crane Boom Angle')"
                            :axis-x="timeConnectionAgl" :axis-y="aglHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('agl', 'upper')"
                            :lower-limit="getChartLimit('agl', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Working Radius -->
                    <b-row v-if="timeConnectionRal.length > 0 && hasNonZeroData(ralHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="m" :title="$t('Crane Working Radius')"
                            :axis-x="timeConnectionRal" :axis-y="ralHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('ral', 'upper')"
                            :lower-limit="getChartLimit('ral', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Actual Load -->
                    <b-row v-if="timeConnectionCag.length > 0 && hasNonZeroData(cagHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="Kg" :title="$t('Crane Actual Load')"
                            :axis-x="timeConnectionCag" :axis-y="cagHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('cag', 'upper')"
                            :lower-limit="getChartLimit('cag', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Rated Capacity -->
                    <b-row v-if="timeConnectionCng.length > 0 && hasNonZeroData(cngHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="Kg" :title="$t('Crane Rated Capacity')"
                            :axis-x="timeConnectionCng" :axis-y="cngHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('cng', 'upper')"
                            :lower-limit="getChartLimit('cng', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Load Percentage -->
                    <b-row v-if="timeConnectionPcg.length > 0 && hasNonZeroData(pcgHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="%" :title="$t('Crane Load Percentage')"
                            :axis-x="timeConnectionPcg" :axis-y="pcgHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('pcg', 'upper')"
                            :lower-limit="getChartLimit('pcg', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Boom Tip Height -->
                    <b-row v-if="timeConnectionAlg.length > 0 && hasNonZeroData(algHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="m" :title="$t('Crane Boom Tip Height')"
                            :axis-x="timeConnectionAlg" :axis-y="algHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('alg', 'upper')"
                            :lower-limit="getChartLimit('alg', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Slew Angle -->
                    <b-row v-if="timeConnectionArg.length > 0 && hasNonZeroData(argHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="°" :title="$t('Crane Slew Angle')"
                            :axis-x="timeConnectionArg" :axis-y="argHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('arg', 'upper')"
                            :lower-limit="getChartLimit('arg', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>

                    <!-- Crane Machine Inclination -->
                    <b-row v-if="timeConnectionIcg.length > 0 && hasNonZeroData(icgHistory)" class="mb-3">
                      <b-col cols="12">
                        <b-card class="chart-card shadow-sm">
                          <complete-indicator-card unity="°" :title="$t('Crane Machine Inclination')"
                            :axis-x="timeConnectionIcg" :axis-y="icgHistory" :use-date-time-scale="true"
                            :use-utc-for-labels="true" :upper-limit="getChartLimit('icg', 'upper')"
                            :lower-limit="getChartLimit('icg', 'lower')" />
                        </b-card>
                      </b-col>
                    </b-row>
                  </div>
                </template>
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <feather-icon icon="SettingsIcon" size="16" class="mr-1" />
                <span>{{ $t('Options') }}</span>
              </template>
              <div class="tab-content-wrapper">
                <div class="asset-options-container">
                  <!-- Seção: Relatórios -->
                  <div class="options-section mb-2">
                    <div class="section-header mb-2">
                      <feather-icon icon="FileTextIcon" size="16" class="text-primary mr-1" />
                      <h6 class="mb-0 font-weight-bold d-inline">{{ $t('Reports') }}</h6>
                    </div>
                    <b-row class="gutter-xs">
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'queries-telemetry', params: { id: myId }, query: { journey: 0 } })"
                          @keydown.enter.prevent="$router.push({ name: 'queries-telemetry', params: { id: myId }, query: { journey: 0 } })"
                          @keydown.space.prevent="$router.push({ name: 'queries-telemetry', params: { id: myId }, query: { journey: 0 } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-primary-light mr-2">
                              <feather-icon icon="MapIcon" size="16" class="text-primary" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Way') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Daily journey report') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-primary" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'queries-telemetry', params: { id: myId }, query: { journey: 0 } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'queries-month', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'queries-month', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'queries-month', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-info-light mr-2">
                              <feather-icon icon="CalendarIcon" size="16" class="text-info" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Monthly Summary') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Monthly telemetry summary') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-info" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'queries-month', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'telemetry-summary', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'telemetry-summary', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'telemetry-summary', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-success-light mr-2">
                              <feather-icon icon="BarChart2Icon" size="16" class="text-success" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Graphic Analysis') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Telemetry summary and charts') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-success" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'telemetry-summary', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'queries-weekly-work-status', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'queries-weekly-work-status', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'queries-weekly-work-status', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-warning-light mr-2">
                              <feather-icon icon="BriefcaseIcon" size="16" class="text-warning" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Work Status') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Weekly work status report') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-warning" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'queries-weekly-work-status', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'telemetry-table', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'telemetry-table', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'telemetry-table', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-secondary-light mr-2">
                              <feather-icon icon="DatabaseIcon" size="16" class="text-secondary" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Telemetry History') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Complete telemetry data history')
                                }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-secondary" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'telemetry-table', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col v-if="hasTcgParameter" md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'queries-payload', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'queries-payload', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'queries-payload', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-primary-light mr-2">
                              <feather-icon icon="TruckIcon" size="16" class="text-primary" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Payload') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Productivity and payload report') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-primary" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'queries-payload', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col v-if="hasCrgParameter" md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'queries-payload-analytics', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'queries-payload-analytics', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'queries-payload-analytics', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-info-light mr-2">
                              <feather-icon icon="BarChart2Icon" size="16" class="text-info" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">Payload Analytics</h6>
                              <small class="text-muted option-subtitle">Análise avançada, ciclos e praças</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="outline-info" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'queries-payload-analytics', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                    </b-row>
                  </div>

                  <!-- Seção: Ferramentas -->
                  <div class="options-section">
                    <div class="section-header mb-2">
                      <feather-icon icon="ToolIcon" size="16" class="text-primary mr-1" />
                      <h6 class="mb-0 font-weight-bold d-inline">{{ $t('Tools') }}</h6>
                    </div>
                    <b-row class="gutter-xs">
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card tool-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="goToAssetAlarms"
                          @keydown.enter.prevent="goToAssetAlarms"
                          @keydown.space.prevent="goToAssetAlarms">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-gradient-primary mr-2">
                              <feather-icon icon="BellIcon" size="16" class="text-white" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Alarms') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('View this asset alarm history') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="gradient-primary" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="goToAssetAlarms">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card tool-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'maintenance', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'maintenance', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'maintenance', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-gradient-primary mr-2">
                              <feather-icon icon="SettingsIcon" size="16" class="text-white" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Maintenance') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Manage maintenance plans') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="gradient-primary" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'maintenance', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card tool-card h-100 cursor-pointer" no-body role="button" tabindex="0"
                          @click="$router.push({ name: 'geo-data', params: { id: myId } })"
                          @keydown.enter.prevent="$router.push({ name: 'geo-data', params: { id: myId } })"
                          @keydown.space.prevent="$router.push({ name: 'geo-data', params: { id: myId } })">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-gradient-primary mr-2">
                              <feather-icon icon="MapIcon" size="16" class="text-white" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Geo Data') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Geospatial Visualization') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(113, 102, 240, 0.15)'" variant="gradient-primary" size="sm"
                              class="ml-1 option-btn"
                              @click.stop="$router.push({ name: 'geo-data', params: { id: myId } })">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                      <b-col v-if="hasBlock" md="4" sm="6" xs="12" class="mb-2">
                        <b-card class="option-card tool-card danger-card h-100 cursor-pointer" no-body role="button"
                          tabindex="0" @click="handleBlock" @keydown.enter.prevent="handleBlock"
                          @keydown.space.prevent="handleBlock">
                          <b-card-body class="d-flex align-items-center p-1">
                            <div class="option-icon-wrapper bg-gradient-danger mr-2">
                              <feather-icon icon="LockIcon" size="16" class="text-white" />
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-0 font-weight-bold option-title">{{ $t('Activate/Deactivate Lock') }}</h6>
                              <small class="text-muted option-subtitle">{{ $t('Control vehicle lock system') }}</small>
                            </div>
                            <b-button v-ripple.400="'rgba(234, 84, 85, 0.15)'" variant="gradient-danger" size="sm"
                              class="ml-1 option-btn" @click.stop="handleBlock">
                              <feather-icon icon="ArrowRightIcon" size="12" />
                            </b-button>
                          </b-card-body>
                        </b-card>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <feather-icon icon="CloudIcon" size="16" class="mr-1" />
                <span>{{ $t('Local Weather') }}</span>
              </template>
              <div class="tab-content-wrapper">
                <div v-if="weatherLoading" class="weather-loading text-center py-5">
                  <b-spinner variant="primary"></b-spinner>
                  <p class="mt-2 text-muted">{{ $t('Loading weather data...') }}</p>
                </div>

                <div v-else-if="weatherError" class="weather-error text-center py-5">
                  <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-2"></feather-icon>
                  <p class="text-muted">{{ weatherError }}</p>
                  <b-button @click="fetchWeatherData" variant="primary" size="sm" class="mt-2">
                    <feather-icon icon="RefreshCwIcon" size="14" class="mr-1"></feather-icon>
                    {{ $t('Try Again') }}
                  </b-button>
                </div>

                <div v-else-if="weatherData" class="weather-container">
                  <!-- Clima Atual -->
                  <b-card class="current-weather-card mb-2" no-body>
                    <b-card-body class="p-2">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <h6 class="mb-0 text-white font-weight-bold">{{ weatherLocation.name }}</h6>
                          <small class="text-white-50" style="font-size: 0.7rem;">{{ weatherLocation.region }}{{
                            weatherLocation.region && weatherLocation.country ? ', ' : '' }}{{ weatherLocation.country
                            }}</small>
                        </div>
                        <b-button @click="fetchWeatherData" variant="outline-light" size="sm"
                          class="d-flex align-items-center justify-content-center p-0"
                          style="min-width: 28px; width: 28px; height: 28px;" v-b-tooltip.hover
                          :title="$t('Refresh weather data')">
                          <feather-icon icon="RefreshCwIcon" size="12"></feather-icon>
                        </b-button>
                      </div>

                      <div class="current-weather-main d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center">
                          <div v-if="weatherCurrent && weatherCurrent.condition && weatherCurrent.condition.icon"
                            class="weather-icon-large mr-2">
                            <img :src="weatherCurrent.condition.icon"
                              :alt="(weatherCurrent.condition && weatherCurrent.condition.text) || ''" />
                          </div>
                          <div>
                            <div class="temperature-display">
                              <span class="temp-value">{{ Math.round((weatherCurrent && weatherCurrent.temp_c) || 0)
                                }}</span>
                              <span class="temp-unit">°C</span>
                            </div>
                            <p class="mb-0 weather-condition">{{ (weatherCurrent && weatherCurrent.condition &&
                              weatherCurrent.condition.text) || 'N/A' }}</p>
                          </div>
                        </div>

                        <div class="weather-details">
                          <div class="weather-detail-item mb-1">
                            <feather-icon icon="DropletIcon" size="12" class="text-white mr-1" v-b-tooltip.hover
                              :title="$t('Relative humidity in the air')">
                            </feather-icon>
                            <span class="detail-value">{{ (weatherCurrent && weatherCurrent.humidity) || 0 }}%</span>
                          </div>
                          <div class="weather-detail-item mb-1">
                            <feather-icon icon="WindIcon" size="12" class="text-white mr-1" v-b-tooltip.hover
                              :title="$t('Wind speed in kilometers per hour')">
                            </feather-icon>
                            <span class="detail-value">{{ Math.round((weatherCurrent && weatherCurrent.wind_kph) || 0)
                              }}
                              km/h</span>
                          </div>
                          <div class="weather-detail-item">
                            <feather-icon icon="EyeIcon" size="12" class="text-white mr-1" v-b-tooltip.hover
                              :title="$t('Visibility distance in kilometers')">
                            </feather-icon>
                            <span class="detail-value">{{ (weatherCurrent && weatherCurrent.vis_km) || 0 }} km</span>
                          </div>
                        </div>
                      </div>
                    </b-card-body>
                  </b-card>

                  <!-- Previsão dos Próximos 5 Dias -->
                  <div class="forecast-section">
                    <h6 class="mb-2 font-weight-bold" style="font-size: 0.875rem;">{{ $t('5-Day Forecast') }}</h6>
                    <div class="forecast-grid">
                      <b-card v-for="(day, index) in weatherForecast" :key="index" class="forecast-card" no-body>
                        <b-card-body class="p-2 text-center">
                          <div class="forecast-date mb-1">
                            <strong style="font-size: 0.75rem;">{{ formatForecastDate(day.date) }}</strong>
                          </div>
                          <div v-if="day.day && day.day.condition && day.day.condition.icon" class="forecast-icon mb-1">
                            <img :src="day.day.condition.icon"
                              :alt="(day.day.condition && day.day.condition.text) || ''" />
                          </div>
                          <div class="forecast-temps mb-1">
                            <div class="temp-max">
                              <feather-icon icon="ArrowUpIcon" size="10" class="text-danger"></feather-icon>
                              <span class="font-weight-bold" style="font-size: 0.875rem;">{{ Math.round((day.day &&
                                day.day.maxtemp_c) || 0) }}°</span>
                            </div>
                            <div class="temp-min ml-1">
                              <feather-icon icon="ArrowDownIcon" size="10" class="text-info"></feather-icon>
                              <span style="font-size: 0.875rem;">{{ Math.round((day.day && day.day.mintemp_c) || 0)
                                }}°</span>
                            </div>
                          </div>
                          <div class="forecast-condition">
                            <small class="text-muted" style="font-size: 0.65rem; line-height: 1.2;">{{ (day.day &&
                              day.day.condition
                              && day.day.condition.text) || 'N/A' }}</small>
                          </div>
                        </b-card-body>
                      </b-card>
                    </div>
                  </div>
                </div>

                <div v-else class="weather-empty text-center py-5">
                  <feather-icon icon="CloudOffIcon" size="48" class="text-muted mb-2"></feather-icon>
                  <p class="text-muted">{{ $t('No weather data available') }}</p>
                  <b-button @click="fetchWeatherData" variant="primary" size="sm">
                    <feather-icon icon="CloudIcon" size="14" class="mr-1"></feather-icon>
                    {{ $t('Load Weather Data') }}
                  </b-button>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </div>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import {
  BTab,
  BTabs,
  BCard,
  BCardBody,
  BAlert,
  BLink,
  BTable,
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BCardText,
  BFormTextarea,
  BSpinner,
  VBTooltip,
  BBadge,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LControl,
  LControlLayers,
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import {
  ref,
  onUnmounted,
  onMounted,
  computed,
  reactive,
  getCurrentInstance,
  watch,
  nextTick
} from '@vue/composition-api'
import router from '@/router'
import store from '@/store'
import SimpleIndicatorCard from "@/views/main/asset/asset-preview/indicators/SimpleIndicatorCard.vue";
import SimpleGaugeIndicator from "@/views/main/asset/asset-preview/indicators/SimpleGaugeIndicator.vue";
import StatusCard from "@/views/main/asset/asset-preview/indicators/StatusCard.vue";
import CompleteIndicatorCard from "@/views/main/asset/asset-preview/indicators/CompleteIndicatorCard.vue";
import assetStoreModule from "@/views/main/asset/assetStoreModule";
import assetModelLimitStoreModule from "@/views/main/asset-model-limit/assetModelLimitStoreModule";
import managementStoreModule from "@/views/main/management/managementStoreModule";
import queriesStoreModule from "@/views/main/queries/queriesStoreModule";
import { resolveIcon } from '@/utils/assetUtils'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { processTelemetryData, telemetryParameterMap } from "@/utils/telemetryParametersUtils";
import { TELEMETRY_CARD_CONFIG } from "@/views/main/asset/asset-preview/config/telemetryCardConfig";
import Ripple from 'vue-ripple-directive'
import i18n from '@/libs/i18n'

export default {
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BTab,
    BTabs,
    BCard,
    BCardBody,
    BAlert,
    BLink,
    BTable,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BCardText,
    BFormTextarea,
    BSpinner,
    BBadge,
    SimpleIndicatorCard,
    SimpleGaugeIndicator,
    StatusCard,
    CompleteIndicatorCard,
    flatPickr,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup,
    LControl,
    LControlLayers,
  },
  methods: {
    registerEventBusListeners: function () {
      this.$root.$on("tlm", this.event_tlm);
    },

    deregisterEventBusListeners: function () {
      this.$root.$off("tlm", this.event_tlm);
    },
  },
  mounted() {
    this.registerEventBusListeners();
  },
  beforeDestroy() {
    this.deregisterEventBusListeners();
  },

  setup() {
    const instance = getCurrentInstance()
    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
    const ASSET_MODEL_LIMIT_STORE_MODULE_NAME = 'app-asset-model-limit'
    const myId = router.currentRoute.params.id
    const user = localStorage.userData != undefined ? JSON.parse(localStorage.userData) : {}

    const assetInfo = ref({})
    const assetSummary = ref({})
    const deviceModemIdentifier = ref('')
    const deviceId = ref('')
    const hasBlock = ref(false)
    const telemetrySummaryLoading = ref(false)
    const telemetrySnapshot = ref({})
    const telemetrySystemMap = ref({}) // Mapeamento de código para systemTypeName
    const odometerHourmeter = ref({
      odometer: null,
      hourmeter: null,
      odometerTimestamp: '',
      hourmeterTimestamp: ''
    })
    const dataWorkStatus = ref([])
    const workStatusLoading = ref(false)
    const workStatusMinTime = ref(null)
    const workStatusMaxTime = ref(null)

    const failuresData = ref({
      recentFailures: []
    })

    const maintenanceData = ref({
      nextMaintenance: null,
      currentValue: 0,
      hasMaintenancePlan: false
    })

    // Variáveis para gráficos diários
    /** Índice da aba principal: 0 Resumo, 1 Painel, 2 Gráfico diário, 3 Opções, 4 Clima */
    const MAIN_TAB_DAILY_CHART = 2
    const mainAssetTabIndex = ref(0)
    /** Evita chamar fetchAssetTelemetry até o usuário abrir a aba Gráfico diário */
    const dailyChartTelemetryFetched = ref(false)
    const summaryParametersList = ref([])
    const telemetryParamsLoading = ref(true)
    const chartDate = ref('')
    const chartLoading = ref(false)
    const telemetryParameters = ref([])
    /** Limites por parâmetro de telemetria para linhas de referência nos gráficos: { [paramCode]: { upper?, lower? } } */
    const chartLimitsMap = ref({})

    // Históricos de tempo e dados para os gráficos
    const timeConnectionRpm = ref([])
    const rpmHistory = ref([])
    const timeConnectionEngine = ref([])
    const engineHistory = ref([])
    const timeConnectionTransmission = ref([])
    const transmissionHistory = ref([])
    const timeConnectionOil = ref([])
    const oilHistory = ref([])
    const timeConnectionCbc = ref([])
    const cbcHistory = ref([])
    const timeConnectionCbt = ref([])
    const cbtHistory = ref([])
    const timeConnectionPcb = ref([])
    const pcbHistory = ref([])
    const timeConnectionPom = ref([])
    const pomHistory = ref([])
    const timeConnectionPtb = ref([])
    const ptbHistory = ref([])
    const timeConnectionTbt = ref([])
    const tbtHistory = ref([])
    const timeConnectionTtb = ref([])
    const ttbHistory = ref([])
    const timeConnectionAcl = ref([])
    const aclHistory = ref([])
    const timeConnectionKmh = ref([])
    const kmhHistory = ref([])
    const timeConnectionCgm = ref([])
    const cgmHistory = ref([])
    const timeConnectionSoc = ref([])
    const socHistory = ref([])
    const timeConnectionTpb = ref([])
    const tpbHistory = ref([])
    const timeConnectionTom = ref([])
    const tomHistory = ref([])
    const timeConnectionTda = ref([])
    const tdaHistory = ref([])
    const timeConnectionPda = ref([])
    const pdaHistory = ref([])
    const timeConnectionPpa = ref([])
    const ppaHistory = ref([])
    const timeConnectionNom = ref([])
    const nomHistory = ref([])
    const timeConnectionDfc = ref([])
    const dfcHistory = ref([])
    const timeConnectionCra = ref([])
    const craHistory = ref([])
    const timeConnectionPff = ref([])
    const pffHistory = ref([])
    const timeConnectionPft = ref([])
    const pftHistory = ref([])
    const timeConnectionPfr = ref([])
    const pfrHistory = ref([])
    const timeConnectionPfm = ref([])
    const pfmHistory = ref([])
    const timeConnectionPdr = ref([])
    const pdrHistory = ref([])
    const timeConnectionTot = ref([])
    const totHistory = ref([])
    const timeConnectionTmt = ref([])
    const tmtHistory = ref([])
    const timeConnectionTc1 = ref([])
    const tc1History = ref([])
    const timeConnectionTc2 = ref([])
    const tc2History = ref([])
    const timeConnectionTc3 = ref([])
    const tc3History = ref([])
    const timeConnectionTpf = ref([])
    const tpfHistory = ref([])
    const timeConnectionTpd = ref([])
    const tpdHistory = ref([])
    const timeConnectionAlt = ref([])
    const altHistory = ref([])
    const timeConnectionRcg = ref([])
    const rcgHistory = ref([])
    const timeConnectionBsc = ref([])
    const bscHistory = ref([])
    const timeConnectionVvt = ref([])
    const vvtHistory = ref([])
    const timeConnectionCpl = ref([])
    const cplHistory = ref([])
    const timeConnectionAgl = ref([])
    const aglHistory = ref([])
    const timeConnectionRal = ref([])
    const ralHistory = ref([])
    const timeConnectionCag = ref([])
    const cagHistory = ref([])
    const timeConnectionCng = ref([])
    const cngHistory = ref([])
    const timeConnectionPcg = ref([])
    const pcgHistory = ref([])
    const timeConnectionAlg = ref([])
    const algHistory = ref([])
    const timeConnectionArg = ref([])
    const argHistory = ref([])
    const timeConnectionIcg = ref([])
    const icgHistory = ref([])

    // Map state
    const latLong = ref([0, 0])
    const center = ref([-19.801187, -43.948885])
    const zoom = ref(5)
    const lastPositionTime = ref('')
    const imageicon = ref('')
    const defaultLayer = ref('map')
    const showLabels = ref(false)
    const showRoads = ref(false)
    const isFullscreen = ref(false)

    // Opções do mapa - desabilitar zoom com scroll por padrão
    const mapOptions = ref({
      scrollWheelZoom: false,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
    })

    // Map URLs
    const arcgisSatelliteUrl = 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
    const arcgisLabelsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'
    const arcgisRoadsUrl = 'https://services.arcgisonline.com/arcgis/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'

    // Icon size
    const iconSize = 40

    // Register modules
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)

    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'
    const QUERIES_APP_STORE_MODULE_NAME = 'app-queries'

    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }

    if (!store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) {
      store.registerModule(QUERIES_APP_STORE_MODULE_NAME, queriesStoreModule)
    }

    if (!store.hasModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME)) {
      store.registerModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME, assetModelLimitStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      if (store.hasModule(QUERIES_APP_STORE_MODULE_NAME)) store.unregisterModule(QUERIES_APP_STORE_MODULE_NAME)
      if (store.hasModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME)) store.unregisterModule(ASSET_MODEL_LIMIT_STORE_MODULE_NAME)
    })

    const $t = (key) => {
      return i18n.t ? i18n.t(key) : (instance?.proxy?.$t ? instance.proxy.$t(key) : key)
    }

    const isMapBaseLayer = (layerName) => {
      if (layerName === 'map' || layerName === $t('Map')) return true
      const lower = String(layerName || '').toLowerCase()
      return lower === 'map' || lower.endsWith('apa')
    }
    const isSatelliteBaseLayer = (layerName) => {
      if (layerName === 'satellite' || layerName === $t('Satellite')) return true
      return String(layerName || '').toLowerCase().startsWith('sat')
    }
    const isLabelsOverlay = (layerName) => layerName === $t('Show locations')
    const isRoadsOverlay = (layerName) => layerName === $t('Show street names')

    const resolveTelemetryDate = (date) => {
      if (!date) return null;
      if (date instanceof Date) {
        return Number.isNaN(date.getTime()) ? null : date;
      }
      const parsed = new Date(date);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const formatTelemetryTimestamp = (entry) => {
      if (!entry) return '';
      const date = entry.receivedAt || resolveTelemetryDate(entry.parameterDate);
      if (!date) return '';

      try {
        return date.toLocaleString(undefined, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (error) {
        return '';
      }
    }

    const updateTelemetryEntry = (code, value, options = {}) => {
      const meta = telemetryParameterMap[code] || {};
      const existing = telemetrySnapshot.value[code] || {};

      const parameterDate = options.parameterDate ?? existing.parameterDate ?? null;
      const receivedAt = resolveTelemetryDate(options.receivedAt)
        || resolveTelemetryDate(parameterDate)
        || existing.receivedAt
        || null;

      const entry = {
        value,
        description: options.description ?? existing.description ?? meta.description ?? '',
        type: options.type ?? existing.type ?? meta.type ?? '',
        parameterDate,
        receivedAt,
      };

      // Criar um novo objeto para garantir reatividade
      telemetrySnapshot.value = {
        ...telemetrySnapshot.value,
        [code]: entry
      };
    }

    const applyTelemetrySnapshot = (entries = []) => {
      if (!Array.isArray(entries)) return;
      entries.forEach(entry => {
        // Armazenar systemTypeName se disponível
        if (entry.systemTypeName) {
          telemetrySystemMap.value[entry.code] = entry.systemTypeName
        }
        updateTelemetryEntry(entry.code, entry.value, {
          description: entry.description,
          type: entry.type,
          parameterDate: entry.parameterDate,
          receivedAt: entry.receivedAt,
        });
      });
    }

    const getTelemetryEntry = (code) => {
      return telemetrySnapshot.value[code] || null
    }

    const getTelemetryValue = (code, fallback = '') => {
      const entry = getTelemetryEntry(code)
      if (!entry || entry.value === null || entry.value === undefined) return fallback
      return entry.value
    }

    const parseNumberOrNull = (value) => {
      if (value === null || value === undefined || value === '') return null
      const n = Number(value)
      return Number.isFinite(n) ? n : null
    }

    // Valores atuais devem vir do AssetSummaryParameter (parameterName: hor/hod)
    const currentHourmeterFromSummaryParam = computed(() => parseNumberOrNull(getTelemetryValue('hor', null)))
    const currentOdometerFromSummaryParam = computed(() => parseNumberOrNull(getTelemetryValue('hod', null)))

    const tryFixDecimalNumbers = (number) => {
      try {
        return parseFloat(number.toFixed(2))
      } catch (error) {
        return number
      }
    }

    const getHoursAndMinutes = (dateString) => {
      const dt = new Date(dateString)
      const dtFormat = dt.toLocaleTimeString().substring(0, 5)
      return dtFormat
    }

    
    const hasNonZeroData = (history) => {
      if (!history || !history.length) return false
      return history.some(v => {
        if (v === null || v === undefined || v === '') return false
        const n = Number(v)
        return !Number.isNaN(n) && n !== 0
      })
    }

    const resetChartData = () => {
      timeConnectionRpm.value = []
      rpmHistory.value = []
      timeConnectionEngine.value = []
      engineHistory.value = []
      timeConnectionTransmission.value = []
      transmissionHistory.value = []
      timeConnectionOil.value = []
      oilHistory.value = []
      timeConnectionCbc.value = []
      cbcHistory.value = []
      timeConnectionCbt.value = []
      cbtHistory.value = []
      timeConnectionPcb.value = []
      pcbHistory.value = []
      timeConnectionPom.value = []
      pomHistory.value = []
      timeConnectionPtb.value = []
      ptbHistory.value = []
      timeConnectionTbt.value = []
      tbtHistory.value = []
      timeConnectionTtb.value = []
      ttbHistory.value = []
      timeConnectionAcl.value = []
      aclHistory.value = []
      timeConnectionKmh.value = []
      kmhHistory.value = []
      timeConnectionCgm.value = []
      cgmHistory.value = []
      timeConnectionSoc.value = []
      socHistory.value = []
      timeConnectionTpb.value = []
      tpbHistory.value = []
      timeConnectionTom.value = []
      tomHistory.value = []
      timeConnectionTda.value = []
      tdaHistory.value = []
      timeConnectionPda.value = []
      pdaHistory.value = []
      timeConnectionPpa.value = []
      ppaHistory.value = []
      timeConnectionNom.value = []
      nomHistory.value = []
      timeConnectionDfc.value = []
      dfcHistory.value = []
      timeConnectionCra.value = []
      craHistory.value = []
      timeConnectionPff.value = []
      pffHistory.value = []
      timeConnectionPft.value = []
      pftHistory.value = []
      timeConnectionPfr.value = []
      pfrHistory.value = []
      timeConnectionPfm.value = []
      pfmHistory.value = []
      timeConnectionPdr.value = []
      pdrHistory.value = []
      timeConnectionTot.value = []
      totHistory.value = []
      timeConnectionTmt.value = []
      tmtHistory.value = []
      timeConnectionTc1.value = []
      tc1History.value = []
      timeConnectionTc2.value = []
      tc2History.value = []
      timeConnectionTc3.value = []
      tc3History.value = []
      timeConnectionTpf.value = []
      tpfHistory.value = []
      timeConnectionTpd.value = []
      tpdHistory.value = []
      timeConnectionAlt.value = []
      altHistory.value = []
      timeConnectionRcg.value = []
      rcgHistory.value = []
      timeConnectionBsc.value = []
      bscHistory.value = []
      timeConnectionVvt.value = []
      vvtHistory.value = []
      timeConnectionCpl.value = []
      cplHistory.value = []
      timeConnectionAgl.value = []
      aglHistory.value = []
      timeConnectionRal.value = []
      ralHistory.value = []
      timeConnectionCag.value = []
      cagHistory.value = []
      timeConnectionCng.value = []
      cngHistory.value = []
      timeConnectionPcg.value = []
      pcgHistory.value = []
      timeConnectionAlg.value = []
      algHistory.value = []
      timeConnectionArg.value = []
      argHistory.value = []
      timeConnectionIcg.value = []
      icgHistory.value = []
    }

    const fetchAssetTelemetry = async (dtS, dtE) => {
      if (isNaN(dtS.getTime())) return

      const startDate = dtS.toISOString()
      const date = new Date(dtS.toISOString())
      date.setUTCHours(23, 59, 0, 0)
      const endDate = date.toISOString()

      /**
       * Converte dateTime do endpoint para timestamp em ms, sem aplicar fuso do cliente.
       * O backend já envia a data/hora no formato de exibição desejado (ex: 2026-02-23T08:52:18.162).
       * Strings sem 'Z' nem offset são tratadas como UTC para que o gráfico exiba exatamente
       * a mesma hora na label e na posição do ponto.
       * - Número < 1e12: tratado como segundos.
       */
      const parseTelemetryDateTime = (value) => {
        if (value == null) return NaN
        if (typeof value === 'number') {
          return value < 1e12 ? value * 1000 : value
        }
        const str = String(value).trim()
        if (!str) return NaN
        if (!/Z|[+-]\d{2}:?\d{2}$/.test(str)) {
          const asUtc = new Date(str + 'Z')
          if (!isNaN(asUtc.getTime())) return asUtc.getTime()
        }
        const d = new Date(str)
        return d.getTime()
      }

      resetChartData()
      chartLoading.value = true

      try {
        for (let i = 0; i < telemetryParameters.value.length; i++) {
          const param = telemetryParameters.value[i]
          let page = 1
          let hasMore = true

          while (hasMore) {
            const response = await store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetTelemetry`, {
              id: myId,
              param: param,
              dtS: startDate,
              dtE: endDate,
              page: page
            })

            if (response && response.data && response.data.data && response.data.data.length > 0) {
              const data = response.data.data
                .slice()
                .sort((a, b) => parseTelemetryDateTime(a.dateTime) - parseTelemetryDateTime(b.dateTime))

              // Processar cada parâmetro
              if (param === 'rpm') {
                data.forEach(item => {
                  timeConnectionRpm.value.push(parseTelemetryDateTime(item.dateTime))
                  rpmHistory.value.push(item.data.rpm)
                })
              } else if (param === 'tpa') {
                data.forEach(item => {
                  timeConnectionEngine.value.push(parseTelemetryDateTime(item.dateTime))
                  engineHistory.value.push(item.data.tpa)
                })
              } else if (param === 'tpt') {
                data.forEach(item => {
                  timeConnectionTransmission.value.push(parseTelemetryDateTime(item.dateTime))
                  transmissionHistory.value.push(item.data.tpt)
                })
              } else if (param === 'toh') {
                data.forEach(item => {
                  timeConnectionOil.value.push(parseTelemetryDateTime(item.dateTime))
                  oilHistory.value.push(item.data.toh)
                })
              } else if (param === 'cbc') {
                data.forEach(item => {
                  timeConnectionCbc.value.push(parseTelemetryDateTime(item.dateTime))
                  cbcHistory.value.push(item.data.cbc)
                })
              } else if (param === 'cbt') {
                data.forEach(item => {
                  timeConnectionCbt.value.push(parseTelemetryDateTime(item.dateTime))
                  cbtHistory.value.push(item.data.cbt)
                })
              } else if (param === 'pcb') {
                data.forEach(item => {
                  timeConnectionPcb.value.push(parseTelemetryDateTime(item.dateTime))
                  pcbHistory.value.push(item.data.pcb)
                })
              } else if (param === 'pom') {
                data.forEach(item => {
                  timeConnectionPom.value.push(parseTelemetryDateTime(item.dateTime))
                  pomHistory.value.push(item.data.pom)
                })
              } else if (param === 'ptb') {
                data.forEach(item => {
                  timeConnectionPtb.value.push(parseTelemetryDateTime(item.dateTime))
                  ptbHistory.value.push(item.data.ptb)
                })
              } else if (param === 'tbt') {
                data.forEach(item => {
                  timeConnectionTbt.value.push(parseTelemetryDateTime(item.dateTime))
                  tbtHistory.value.push(item.data.tbt)
                })
              } else if (param === 'ttb') {
                data.forEach(item => {
                  timeConnectionTtb.value.push(parseTelemetryDateTime(item.dateTime))
                  ttbHistory.value.push(item.data.ttb)
                })
              } else if (param === 'acl') {
                data.forEach(item => {
                  timeConnectionAcl.value.push(parseTelemetryDateTime(item.dateTime))
                  const aclValue = item.data.acl
                  const truncatedAcl = Math.trunc(aclValue * 100) / 100
                  aclHistory.value.push(truncatedAcl)
                })
              } else if (param === 'kmh') {
                data.forEach(item => {
                  timeConnectionKmh.value.push(parseTelemetryDateTime(item.dateTime))
                  kmhHistory.value.push(item.data.kmh)
                })
              } else if (param === 'cgm') {
                data.forEach(item => {
                  timeConnectionCgm.value.push(parseTelemetryDateTime(item.dateTime))
                  cgmHistory.value.push(item.data.cgm)
                })
              } else if (param === 'soc') {
                data.forEach(item => {
                  timeConnectionSoc.value.push(parseTelemetryDateTime(item.dateTime))
                  socHistory.value.push(item.data.soc)
                })
              } else if (param === 'tpb') {
                data.forEach(item => {
                  timeConnectionTpb.value.push(parseTelemetryDateTime(item.dateTime))
                  tpbHistory.value.push(item.data.tpb)
                })
              } else if (param === 'tom') {
                data.forEach(item => {
                  timeConnectionTom.value.push(parseTelemetryDateTime(item.dateTime))
                  tomHistory.value.push(item.data.tom)
                })
              } else if (param === 'tda') {
                data.forEach(item => {
                  timeConnectionTda.value.push(parseTelemetryDateTime(item.dateTime))
                  tdaHistory.value.push(item.data.tda)
                })
              } else if (param === 'pda') {
                data.forEach(item => {
                  timeConnectionPda.value.push(parseTelemetryDateTime(item.dateTime))
                  pdaHistory.value.push(item.data.pda)
                })
              } else if (param === 'ppa') {
                data.forEach(item => {
                  timeConnectionPpa.value.push(parseTelemetryDateTime(item.dateTime))
                  ppaHistory.value.push(item.data.ppa)
                })
              } else if (param === 'nom') {
                data.forEach(item => {
                  timeConnectionNom.value.push(parseTelemetryDateTime(item.dateTime))
                  nomHistory.value.push(item.data.nom)
                })
              } else if (param === 'dfc') {
                data.forEach(item => {
                  timeConnectionDfc.value.push(parseTelemetryDateTime(item.dateTime))
                  dfcHistory.value.push(item.data.dfc)
                })
              } else if (param === 'cra') {
                data.forEach(item => {
                  timeConnectionCra.value.push(parseTelemetryDateTime(item.dateTime))
                  craHistory.value.push(item.data.cra)
                })
              } else if (param === 'pff') {
                data.forEach(item => {
                  timeConnectionPff.value.push(parseTelemetryDateTime(item.dateTime))
                  pffHistory.value.push(item.data.pff)
                })
              } else if (param === 'pft') {
                data.forEach(item => {
                  timeConnectionPft.value.push(parseTelemetryDateTime(item.dateTime))
                  pftHistory.value.push(item.data.pft)
                })
              } else if (param === 'pfr') {
                data.forEach(item => {
                  timeConnectionPfr.value.push(parseTelemetryDateTime(item.dateTime))
                  pfrHistory.value.push(item.data.pfr)
                })
              } else if (param === 'pfm') {
                data.forEach(item => {
                  timeConnectionPfm.value.push(parseTelemetryDateTime(item.dateTime))
                  pfmHistory.value.push(item.data.pfm)
                })
              } else if (param === 'pdr') {
                data.forEach(item => {
                  timeConnectionPdr.value.push(parseTelemetryDateTime(item.dateTime))
                  pdrHistory.value.push(item.data.pdr)
                })
              } else if (param === 'tot') {
                data.forEach(item => {
                  timeConnectionTot.value.push(parseTelemetryDateTime(item.dateTime))
                  totHistory.value.push(item.data.tot)
                })
              } else if (param === 'tmt') {
                data.forEach(item => {
                  timeConnectionTmt.value.push(parseTelemetryDateTime(item.dateTime))
                  tmtHistory.value.push(item.data.tmt)
                })
              } else if (param === 'tc1') {
                data.forEach(item => {
                  timeConnectionTc1.value.push(parseTelemetryDateTime(item.dateTime))
                  tc1History.value.push(item.data.tc1)
                })
              } else if (param === 'tc2') {
                data.forEach(item => {
                  timeConnectionTc2.value.push(parseTelemetryDateTime(item.dateTime))
                  tc2History.value.push(item.data.tc2)
                })
              } else if (param === 'tc3') {
                data.forEach(item => {
                  timeConnectionTc3.value.push(parseTelemetryDateTime(item.dateTime))
                  tc3History.value.push(item.data.tc3)
                })
              } else if (param === 'tpf') {
                data.forEach(item => {
                  timeConnectionTpf.value.push(parseTelemetryDateTime(item.dateTime))
                  tpfHistory.value.push(item.data.tpf)
                })
              } else if (param === 'tpd') {
                data.forEach(item => {
                  timeConnectionTpd.value.push(parseTelemetryDateTime(item.dateTime))
                  tpdHistory.value.push(item.data.tpd)
                })
              } else if (param === 'alt') {
                data.forEach(item => {
                  timeConnectionAlt.value.push(parseTelemetryDateTime(item.dateTime))
                  altHistory.value.push(item.data.alt)
                })
              } else if (param === 'rcg') {
                data.forEach(item => {
                  timeConnectionRcg.value.push(parseTelemetryDateTime(item.dateTime))
                  rcgHistory.value.push(item.data.rcg)
                })
              } else if (param === 'bsc') {
                data.forEach(item => {
                  timeConnectionBsc.value.push(parseTelemetryDateTime(item.dateTime))
                  bscHistory.value.push(item.data.bsc)
                })
              } else if (param === 'vvt') {
                data.forEach(item => {
                  timeConnectionVvt.value.push(parseTelemetryDateTime(item.dateTime))
                  vvtHistory.value.push(item.data.vvt)
                })
              } else if (param === 'cpl') {
                data.forEach(item => {
                  timeConnectionCpl.value.push(parseTelemetryDateTime(item.dateTime))
                  cplHistory.value.push(item.data.cpl)
                })
              } else if (param === 'agl') {
                data.forEach(item => {
                  timeConnectionAgl.value.push(parseTelemetryDateTime(item.dateTime))
                  aglHistory.value.push(item.data.agl)
                })
              } else if (param === 'ral') {
                data.forEach(item => {
                  timeConnectionRal.value.push(parseTelemetryDateTime(item.dateTime))
                  ralHistory.value.push(item.data.ral)
                })
              } else if (param === 'cag') {
                data.forEach(item => {
                  timeConnectionCag.value.push(parseTelemetryDateTime(item.dateTime))
                  cagHistory.value.push(item.data.cag)
                })
              } else if (param === 'cng') {
                data.forEach(item => {
                  timeConnectionCng.value.push(parseTelemetryDateTime(item.dateTime))
                  cngHistory.value.push(item.data.cng)
                })
              } else if (param === 'pcg') {
                data.forEach(item => {
                  timeConnectionPcg.value.push(parseTelemetryDateTime(item.dateTime))
                  pcgHistory.value.push(item.data.pcg)
                })
              } else if (param === 'alg') {
                data.forEach(item => {
                  timeConnectionAlg.value.push(parseTelemetryDateTime(item.dateTime))
                  algHistory.value.push(item.data.alg)
                })
              } else if (param === 'arg') {
                data.forEach(item => {
                  timeConnectionArg.value.push(parseTelemetryDateTime(item.dateTime))
                  argHistory.value.push(item.data.arg)
                })
              } else if (param === 'icg') {
                data.forEach(item => {
                  timeConnectionIcg.value.push(parseTelemetryDateTime(item.dateTime))
                  icgHistory.value.push(item.data.icg)
                })
              }

              // Verificar se há mais páginas
              if (response.data.data.length < 100) {
                hasMore = false
              } else {
                page++
              }
            } else {
              hasMore = false
            }
          }
        }
      } catch (_) { } finally {
        chartLoading.value = false
      }
    }

    const fetchChartHistoric = () => {
      if (!chartDate.value) return

      const [year, month, day] = chartDate.value.split('-').map(Number)
      const dts = new Date(Date.UTC(year, month - 1, day))
      const dtf = new Date(chartDate.value)
      dtf.setHours(23)
      dtf.setMinutes(59)
      dtf.setDate(dtf.getDate())

      fetchAssetTelemetry(dts, dtf)
    }

    /**
     * Carrega telemetria do gráfico diário só após o usuário abrir a aba correspondente
     * (e quando os parâmetros já foram obtidos).
     */
    const tryLoadDailyChartTelemetry = () => {
      if (mainAssetTabIndex.value !== MAIN_TAB_DAILY_CHART) return
      if (!chartDate.value) return
      if (!telemetryParameters.value.length) return
      if (dailyChartTelemetryFetched.value) return
      dailyChartTelemetryFetched.value = true
      fetchChartHistoric()
    }

    watch([mainAssetTabIndex, () => telemetryParameters.value.length], tryLoadDailyChartTelemetry)

    const onChartDateChange = () => {
      if (!dailyChartTelemetryFetched.value) return
      fetchChartHistoric()
    }

    const getChartLimit = (param, type) => {
      const limits = chartLimitsMap.value[param]
      return limits && limits[type] != null ? limits[type] : undefined
    }

    /**
     * Busca limites do modelo do asset e preenche chartLimitsMap para linhas de referência nos gráficos diários.
     * Só considera limites cujo telemetryParameterName está contido na lista de parâmetros (fetchAssetTelemetryParameters).
     */
    const fetchChartLimits = () => {
      const modelId = assetInfo.value?.modelId ?? assetInfo.value?.assetModelId
      const params = telemetryParameters.value || []
      const paramCodes = params
        .map(p => (typeof p === 'string' ? p : (p && (p.code ?? p.name ?? p))))
        .filter(Boolean)
        .map(s => String(s).toLowerCase())
      if (!modelId || paramCodes.length === 0) {
        chartLimitsMap.value = {}
        return
      }
      store
        .dispatch(`${ASSET_MODEL_LIMIT_STORE_MODULE_NAME}/fetchSearchAssetModelLimit`, {
          assetModelId: modelId,
          page: 1,
          perPage: 500
        })
        .then(response => {
          const list = response?.data?.data ?? response?.data ?? []
          const items = Array.isArray(list) ? list : []
          const map = {}
          items.forEach(limit => {
            const paramName = String(limit.telemetryParameterName ?? limit.telemetryParameter ?? '').trim().toLowerCase()
            if (!paramName || !paramCodes.includes(paramName)) return
            const limitType = (limit.limitType ?? limit.limitTypeName ?? '').toString().trim()
            const value = limit.value != null ? Number(limit.value) : NaN
            if (!Number.isFinite(value)) return
            if (!map[paramName]) map[paramName] = { upper: null, lower: null }
            if (limitType === 'Upper Limit') map[paramName].upper = value
            else if (limitType === 'Lower Limit') map[paramName].lower = value
          })
          chartLimitsMap.value = map
        })
        .catch(() => {
          chartLimitsMap.value = {}
        })
    }

    const flatpickrConfig = {
      dateFormat: 'Y-m-d',
      maxDate: 'today',
      locale: {
        firstDayOfWeek: 1
      }
    }

    const hasChartData = computed(() => {
      return (
        (timeConnectionRpm.value.length > 0 && rpmHistory.value.length > 0) ||
        (timeConnectionEngine.value.length > 0 && engineHistory.value.length > 0) ||
        (timeConnectionTransmission.value.length > 0 && transmissionHistory.value.length > 0) ||
        (timeConnectionOil.value.length > 0 && oilHistory.value.length > 0) ||
        (timeConnectionCbc.value.length > 0 && cbcHistory.value.length > 0) ||
        (timeConnectionCbt.value.length > 0 && cbtHistory.value.length > 0) ||
        (timeConnectionPcb.value.length > 0 && pcbHistory.value.length > 0) ||
        (timeConnectionPom.value.length > 0 && pomHistory.value.length > 0) ||
        (timeConnectionPtb.value.length > 0 && ptbHistory.value.length > 0) ||
        (timeConnectionTbt.value.length > 0 && tbtHistory.value.length > 0) ||
        (timeConnectionTtb.value.length > 0 && ttbHistory.value.length > 0) ||
        (timeConnectionAcl.value.length > 0 && aclHistory.value.length > 0) ||
        (timeConnectionKmh.value.length > 0 && kmhHistory.value.length > 0) ||
        (timeConnectionCgm.value.length > 0 && cgmHistory.value.length > 0) ||
        (timeConnectionSoc.value.length > 0 && socHistory.value.length > 0) ||
        (timeConnectionTpb.value.length > 0 && tpbHistory.value.length > 0) ||
        (timeConnectionTom.value.length > 0 && tomHistory.value.length > 0) ||
        (timeConnectionTda.value.length > 0 && tdaHistory.value.length > 0) ||
        (timeConnectionPda.value.length > 0 && pdaHistory.value.length > 0) ||
        (timeConnectionPpa.value.length > 0 && ppaHistory.value.length > 0) ||
        (timeConnectionNom.value.length > 0 && nomHistory.value.length > 0) ||
        (timeConnectionDfc.value.length > 0 && dfcHistory.value.length > 0) ||
        (timeConnectionCra.value.length > 0 && craHistory.value.length > 0) ||
        (timeConnectionPff.value.length > 0 && pffHistory.value.length > 0) ||
        (timeConnectionPft.value.length > 0 && pftHistory.value.length > 0) ||
        (timeConnectionPfr.value.length > 0 && pfrHistory.value.length > 0) ||
        (timeConnectionPfm.value.length > 0 && pfmHistory.value.length > 0) ||
        (timeConnectionPdr.value.length > 0 && pdrHistory.value.length > 0) ||
        (timeConnectionTot.value.length > 0 && totHistory.value.length > 0) ||
        (timeConnectionTmt.value.length > 0 && tmtHistory.value.length > 0) ||
        (timeConnectionTc1.value.length > 0 && tc1History.value.length > 0) ||
        (timeConnectionTc2.value.length > 0 && tc2History.value.length > 0) ||
        (timeConnectionTc3.value.length > 0 && tc3History.value.length > 0) ||
        (timeConnectionTpf.value.length > 0 && tpfHistory.value.length > 0) ||
        (timeConnectionTpd.value.length > 0 && tpdHistory.value.length > 0) ||
        (timeConnectionAlt.value.length > 0 && altHistory.value.length > 0) ||
        (timeConnectionRcg.value.length > 0 && rcgHistory.value.length > 0) ||
        (timeConnectionBsc.value.length > 0 && bscHistory.value.length > 0) ||
        (timeConnectionVvt.value.length > 0 && vvtHistory.value.length > 0) ||
        (timeConnectionCpl.value.length > 0 && cplHistory.value.length > 0) ||
        (timeConnectionAgl.value.length > 0 && aglHistory.value.length > 0) ||
        (timeConnectionRal.value.length > 0 && ralHistory.value.length > 0) ||
        (timeConnectionCag.value.length > 0 && cagHistory.value.length > 0) ||
        (timeConnectionCng.value.length > 0 && cngHistory.value.length > 0) ||
        (timeConnectionPcg.value.length > 0 && pcgHistory.value.length > 0) ||
        (timeConnectionAlg.value.length > 0 && algHistory.value.length > 0) ||
        (timeConnectionArg.value.length > 0 && argHistory.value.length > 0) ||
        (timeConnectionIcg.value.length > 0 && icgHistory.value.length > 0)
      )
    })

    const formatTelemetryValue = (value, config) => {
      if (config && typeof config.valueFormatter === 'function') {
        return config.valueFormatter(value);
      }
      if (value === null || value === undefined) return value;
      if (typeof value === 'number' && Number.isFinite(value)) {
        if (config && typeof config.decimals === 'number') {
          return value.toFixed(config.decimals);
        }
        return value;
      }
      return value;
    }

    const buildTelemetryCard = (config) => {
      const entry = telemetrySnapshot.value[config.code];
      if (!entry || entry.value === null || entry.value === undefined) return null;

      const rawValue = entry.value;
      const numericValue = Number(rawValue);
      const isNumeric = !Number.isNaN(numericValue);
      const isZeroValue = (isNumeric && numericValue === 0) || rawValue === 0 || rawValue === '0';

      if (isZeroValue) return null;
      // btf só aparece quando é true (bateria fraca)
      if (config.code === 'btf' && !rawValue) return null;
      // tct só aparece quando é > 1 (falha ativa)
      if (config.code === 'tct' && (!rawValue || rawValue <= 1)) return null;

      const formattedValue = formatTelemetryValue(rawValue, config);
      const timestamp = formatTelemetryTimestamp(entry);

      if (config.component === 'gauge') {
        if (!isNumeric || numericValue <= 0) return null;
        return {
          ...config,
          component: 'gauge',
          title: $t(config.labelKey),
          value: numericValue,
          timestamp,
        };
      }

      const card = {
        ...config,
        component: 'simple',
        title: $t(config.labelKey),
        icon: config.icon,
        color: config.color || 'info',
        value: config.hideValue ? '' : formattedValue,
        timestamp,
        unit: config.unitLabel ? $t(config.unitLabel) : '',
      };

      if (config.valueLabelKey) {
        card.value = $t(config.valueLabelKey);
      }

      return card;
    }

    const telemetryCards = computed(() => {
      return TELEMETRY_CARD_CONFIG
        .map(config => buildTelemetryCard(config))
        .filter(card => card !== null)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
    })

    const hasTelemetryCards = computed(() => {
      return telemetryCards.value.length > 0;
    })

    const fetchAsset = () => {
      return store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAsset`, { id: myId }).then(response => {
        if (response && response.data) {
          if (response.data.devices && response.data.devices.length > 0) {
            let identifiers = ''
            response.data.devices.forEach(device => {
              identifiers += `${device.modemIdentifier} `
            })
            deviceModemIdentifier.value = identifiers.trim()
            // Obter deviceId do primeiro dispositivo
            if (response.data.devices[0] && response.data.devices[0].id) {
              deviceId.value = response.data.devices[0].id
            }
          }
          // Obter hasBlock se disponível
          if (response.data.hasBlock !== undefined) {
            hasBlock.value = response.data.hasBlock
          }
          assetInfo.value = response.data
          fetchChartLimits()
          // Garantir que o ícone seja carregado de forma reativa
          nextTick(() => {
            imageicon.value = resolveIcon(response.data.assetType, response.data.iconUrl)
          })
        }
      }).catch(() => { })
    }

    // Estado do clima
    const weatherData = ref(null)
    const weatherLoading = ref(false)
    const weatherError = ref(null)
    const weatherForecast = computed(() => {
      if (weatherData.value && weatherData.value.forecast && weatherData.value.forecast.forecastday) {
        // Retornar os próximos 5 dias
        return weatherData.value.forecast.forecastday.slice(0, 5)
      }
      return []
    })

    const fetchWeatherData = () => {
      // Usar coordenadas do assetSummary se disponíveis
      const lat = (assetSummary.value && assetSummary.value.lastLat) || latLong.value[0]
      const lng = (assetSummary.value && assetSummary.value.lastLng) || latLong.value[1]

      // Verificar se temos coordenadas válidas
      if (!lat || !lng || lat === 0 || lng === 0) {
        weatherError.value = instance.proxy.$t('No location data available to fetch weather')
        return
      }

      weatherLoading.value = true
      weatherError.value = null

      return store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchWeatherForecast`, {
        lat,
        lng,
        days: 5
      }).then(response => {
        if (response && response.data) {
          weatherData.value = response.data
          weatherError.value = null
        } else {
          weatherError.value = instance.proxy.$t('No weather data received')
        }
      }).catch((e) => {
        weatherError.value = instance.proxy.$t('Error loading weather data')
      }).finally(() => {
        weatherLoading.value = false
      })
    }

    const formatForecastDate = (dateString) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        const today = new Date()
        const tomorrow = new Date(today)
        tomorrow.setDate(tomorrow.getDate() + 1)

        // Verificar se é hoje, amanhã ou outro dia
        if (date.toDateString() === today.toDateString()) {
          return instance.proxy.$t('Today')
        } else if (date.toDateString() === tomorrow.toDateString()) {
          return instance.proxy.$t('Tomorrow')
        } else {
          return date.toLocaleDateString(getIntlLocale(), {
            weekday: 'short',
            day: '2-digit',
            month: 'short'
          })
        }
      } catch (error) {
        return dateString
      }
    }

    // Métodos auxiliares para acessar dados do clima de forma segura
    const getWeatherLocation = () => {
      if (!weatherData.value || !weatherData.value.location) return { name: 'N/A', region: '', country: '' }
      return {
        name: weatherData.value.location.name || 'N/A',
        region: weatherData.value.location.region || '',
        country: weatherData.value.location.country || ''
      }
    }

    const getWeatherCurrent = () => {
      if (!weatherData.value || !weatherData.value.current) return null
      return weatherData.value.current
    }

    const weatherLocation = computed(() => getWeatherLocation())
    const weatherCurrent = computed(() => getWeatherCurrent())

    // Formatar período de datas do status de trabalho
    const workStatusPeriod = computed(() => {
      if (!workStatusMinTime.value || !workStatusMaxTime.value) return ''

      try {
        const minDate = new Date(workStatusMinTime.value)
        const maxDate = new Date(workStatusMaxTime.value)

        if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return ''

        const minFormatted = minDate.toLocaleDateString(getIntlLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })

        const maxFormatted = maxDate.toLocaleDateString(getIntlLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })

        return `${minFormatted} ${$t('until')} ${maxFormatted}`
      } catch (error) {
        return ''
      }
    })

    const formatDate = (myDate) => {
      if (!myDate) return ''
      try {
        const d = new Date(myDate)
        if (Number.isNaN(d.getTime())) return ''
        let dt = d.toLocaleDateString(getIntlLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
        const hm = d.toLocaleTimeString(getIntlLocale(), {
          hour: '2-digit',
          minute: '2-digit'
        })
        return `${dt} - ${hm}`
      } catch (error) {
        return ''
      }
    }

    const formatFailureDate = (date) => {
      if (!date) return ''
      try {
        const d = new Date(date)
        if (Number.isNaN(d.getTime())) return ''
        return d.toLocaleDateString(getIntlLocale(), {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return ''
      }
    }

    const getFailureTypeLabel = (failureType) => {
      if (failureType === 2) {
        return $t('Attention')
      } else if (failureType === 3) {
        return $t('Mandatory Stop')
      }
      return ''
    }

    // Função para buscar falhas dos últimos 7 dias
    const fetchFailuresData = async () => {
      try {
        const today = new Date()
        const sevenDaysAgo = new Date(today)
        sevenDaysAgo.setDate(today.getDate() - 7)

        const dayStart = sevenDaysAgo.toISOString().split('T')[0]
        const dayEnd = today.toISOString().split('T')[0]

        // Buscar falhas de nível 2 e 3
        const response = await store.dispatch(`${QUERIES_APP_STORE_MODULE_NAME}/fetchFailureReport`, {
          assetId: myId,
          dayStart,
          dayEnd,
          page: 1,
          perPage: 10,
          failureTypeList: [2, 3] // Nível 2 (Atenção) e Nível 3 (Parada Obrigatória)
        })

        if (response && response.data && response.data.items && response.data.items.length > 0) {
          const items = response.data.items

          // Pegar as últimas 3 falhas (ordenadas por data mais recente)
          const recentFailures = items
            .sort((a, b) => new Date(b.dateTime || b.date || b.datetime) - new Date(a.dateTime || a.date || a.datetime))
            .slice(0, 3)
            .map(item => ({
              failureCode: item.failureCode || item.code || 'N/A',
              failureDate: item.dateTime || item.date || item.datetime,
              failureDescription: item.failureDescription || item.description || '',
              failureType: item.failureType || item.failureLevel || item.level || 0
            }))

          failuresData.value = {
            recentFailures
          }
        }
      } catch (error) {
        failuresData.value = {
          recentFailures: []
        }
      }
    }

    // Função para buscar dados de manutenção
    const fetchMaintenanceData = async () => {
      try {
        const response = await store.dispatch(`${MANAGEMENT_APP_STORE_MODULE_NAME}/fetchMaintenancePlanSummary`, {
          id: myId
        })

        if (response && response.data) {
          const currentHourmeter = response.data.currentHourmeter
          const currentOdometer = response.data.currentOdometer
          const maintenancePlans = response.data.data || []

          if (maintenancePlans.length > 0) {
            // Pegar o primeiro da lista (mais próximo)
            const nextPlan = maintenancePlans[0]
            const isHourmeter = !!nextPlan.hourmeter
            const scheduledValue = parseNumberOrNull(isHourmeter ? nextPlan.hourmeter : nextPlan.odometer) ?? 0

            // Fonte preferencial: assetSummaryParameter (hor/hod). Fallback: valores do endpoint do plano.
            const currentFromParam = isHourmeter ? currentHourmeterFromSummaryParam.value : currentOdometerFromSummaryParam.value
            const currentFromApi = parseNumberOrNull(isHourmeter ? currentHourmeter : currentOdometer)
            const currentValue = (currentFromParam ?? currentFromApi ?? 0)

            // Se não houver hor/hod no summaryParameter, usar o *Left da API do plano (mais confiável do que "scheduled - 0")
            const remainingFromApi = parseNumberOrNull(isHourmeter ? nextPlan.hourmeterLeft : nextPlan.odometerLeft)
            const remaining = (currentFromParam != null)
              ? (scheduledValue - currentValue)
              : (remainingFromApi ?? (scheduledValue - currentValue))

            maintenanceData.value = {
              nextMaintenance: {
                planName: nextPlan.planName || '',
                name: nextPlan.name || '',
                scheduledValue,
                remaining,
                isHourmeter
              },
              currentValue,
              hasMaintenancePlan: true
            }
          } else {
            // Não há plano de manutenção cadastrado
            maintenanceData.value = {
              nextMaintenance: null,
              currentValue: 0,
              hasMaintenancePlan: false
            }
          }
        }
      } catch (error) {
        maintenanceData.value = {
          nextMaintenance: null,
          currentValue: 0,
          hasMaintenancePlan: false
        }
      }
    }

    // Recalcular automaticamente quando o horímetro/odômetro (summaryParameter) atualizar
    watch(
      [
        currentHourmeterFromSummaryParam,
        currentOdometerFromSummaryParam,
        () => maintenanceData.value?.nextMaintenance?.scheduledValue,
        () => maintenanceData.value?.nextMaintenance?.isHourmeter,
      ],
      () => {
        const next = maintenanceData.value?.nextMaintenance
        if (!next) return

        const scheduledValue = parseNumberOrNull(next.scheduledValue) ?? 0
        const isHourmeter = !!next.isHourmeter
        const currentValue = (isHourmeter ? currentHourmeterFromSummaryParam.value : currentOdometerFromSummaryParam.value)
        // Sem hor/hod no JSON: manter o que veio da API do plano, sem recalcular
        if (currentValue === null) return

        maintenanceData.value = {
          ...maintenanceData.value,
          currentValue,
          nextMaintenance: {
            ...next,
            scheduledValue,
            remaining: scheduledValue - currentValue,
          },
        }
      },
      { immediate: true }
    )


    const toGoogleMap = () => {
      if (latLong.value && latLong.value[0] && latLong.value[1]) {
        const url = `https://www.google.com/maps?q=${latLong.value[0]},${latLong.value[1]}`
        window.open(url, '_blank')
      }
    }

    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value
      // Forçar atualização do tamanho do mapa quando entrar/sair do fullscreen
      nextTick(() => {
        if (instance?.proxy?.$refs?.summaryMap?.mapObject) {
          setTimeout(() => {
            instance.proxy.$refs.summaryMap.mapObject.invalidateSize()
          }, 300)
        }
      })
    }

    // Handlers para controlar zoom com scroll
    let keyDownHandler = null
    let keyUpHandler = null

    const onMapReady = () => {
      const map = instance?.proxy?.$refs?.summaryMap?.mapObject
      if (!map) return

      // Garantir que scrollWheelZoom está desabilitado inicialmente
      if (map.scrollWheelZoom) {
        map.scrollWheelZoom.disable()
      }

      // Adicionar listener para detectar quando Ctrl está pressionado
      keyDownHandler = (e) => {
        if ((e.ctrlKey || e.metaKey) && map.scrollWheelZoom) {
          // Habilitar zoom com scroll quando Ctrl (ou Cmd no Mac) está pressionado
          map.scrollWheelZoom.enable()
        }
      }

      keyUpHandler = (e) => {
        if (!e.ctrlKey && !e.metaKey && map.scrollWheelZoom) {
          // Desabilitar zoom com scroll quando Ctrl não está mais pressionado
          map.scrollWheelZoom.disable()
        }
      }

      // Adicionar listeners globais
      document.addEventListener('keydown', keyDownHandler)
      document.addEventListener('keyup', keyUpHandler)

      // Também configurar listeners no próprio mapa para garantir que funcione
      map.on('wheel', (e) => {
        if (!e.originalEvent.ctrlKey && !e.originalEvent.metaKey) {
          e.originalEvent.preventDefault()
        }
      })
    }

    // Limpar listeners quando o componente for desmontado
    onUnmounted(() => {
      if (keyDownHandler) {
        document.removeEventListener('keydown', keyDownHandler)
      }
      if (keyUpHandler) {
        document.removeEventListener('keyup', keyUpHandler)
      }
    })

    const updateMapPosition = () => {
      if (latLong.value && latLong.value[0] && latLong.value[1] && latLong.value[0] !== 0 && latLong.value[1] !== 0) {
        // Não atualizar center/zoom aqui, apenas fazer fitBounds com animação suave
        nextTick(() => {
          if (instance?.proxy?.$refs?.summaryMap?.mapObject) {
            const map = instance.proxy.$refs.summaryMap.mapObject
            // fitBounds precisa de um array de coordenadas [lat, lng]
            const bounds = [
              [latLong.value[0], latLong.value[1]]
            ]
            // Usar flyToBounds para uma transição mais suave e visualmente agradável
            if (map.flyToBounds) {
              map.flyToBounds(bounds, {
                padding: [50, 50],
                maxZoom: 17,
                duration: 1.5
              })
            } else {
              // Fallback para fitBounds se flyToBounds não estiver disponível
              map.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 17,
                animate: true,
                duration: 1.5,
                easeLinearity: 0.25
              })
            }
          }
        })
      }
    }

    const dynamicSize = computed(() => {
      return [iconSize, iconSize * 1.15]
    })

    const dynamicAnchor = computed(() => {
      return [27, 30]
    })

    const dynamicTooltip = computed(() => {
      return [25, 0]
    })

    const dynamicPopup = computed(() => {
      return [2, -27]
    })

    const handleImageError = (e) => {
    }

    const handleImageLoad = () => {
    }

    const fetchAssetSummary = () => {
      return store
        .dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetSummary`, {
          id: myId
        })
        .then(response => {
          if (response && response.data) {
            assetSummary.value = response.data

            // Atualizar posição do mapa de forma reativa
            if (response.data.lastLat && response.data.lastLat !== 0 &&
              response.data.lastLng && response.data.lastLng !== 0) {
              // Criar novo array para garantir reatividade
              latLong.value = [response.data.lastLat, response.data.lastLng]
              // Aguardar o mapa estar totalmente carregado antes de fazer a transição suave
              setTimeout(() => {
                updateMapPosition()
              }, 800)

              // Buscar dados do clima automaticamente após obter coordenadas válidas
              fetchWeatherData()
            }

            // Atualizar data da última posição
            if (response.data.lastPositionTime) {
              lastPositionTime.value = formatDate(response.data.lastPositionTime)
            }
          }
        })
        .catch(() => { })
    }

    const fetchTelemetrySummaryParameter = () => {
      telemetrySummaryLoading.value = true;
      return store
        .dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetSummaryParameter`, { assetId: myId })
        .then(response => {
          const rawData = response?.data?.data || [];
          summaryParametersList.value = rawData;
          const telemetryData = processTelemetryData(rawData);
          applyTelemetrySnapshot(telemetryData);

          // Extrair odômetro e horímetro
          const hodEntry = telemetryData.find(item => item.code === 'hod');
          const horEntry = telemetryData.find(item => item.code === 'hor');

          if (hodEntry && hodEntry.value !== null && hodEntry.value !== undefined) {
            odometerHourmeter.value.odometer = hodEntry.value.toString();
            odometerHourmeter.value.odometerTimestamp = formatTelemetryTimestamp(hodEntry);
          }

          if (horEntry && horEntry.value !== null && horEntry.value !== undefined) {
            odometerHourmeter.value.hourmeter = horEntry.value.toString();
            odometerHourmeter.value.hourmeterTimestamp = formatTelemetryTimestamp(horEntry);
          }
        })
        .catch(() => { })
        .finally(() => {
          telemetrySummaryLoading.value = false;
        })
    }

    const resolveColor = (status) => {
      if (!status) return '#f3f2f7'
      if (status === 'failure') return '#ea5455'
      if (status === 'off') return '#82868b'
      if (status === 'noconnection') return '#f3f2f7'
      if (status === 'severe') return '#ff9f43'
      if (status === 'iddle') return '#00cfe8'
      if (status === 'on') return '#28c76f'
      return '#28c76f'
    }

    const getStatusVariant = (status) => {
      if (!status || status === 'noconnection') return 'light'
      if (status === 'failure') return 'danger'
      if (status === 'off') return 'secondary'
      if (status === 'severe') return 'warning'
      if (status === 'iddle') return 'info'
      if (status === 'on') return 'success'
      return 'primary'
    }

    const fetchAssetWorkstatusHistory = async () => {
      workStatusLoading.value = true
      dataWorkStatus.value = []

      try {
        // Calcular datas: hoje, ontem e anteontem
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        const twoDaysAgo = new Date(today)
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)

        // Definir minTime e maxTime para os 3 dias (antecntem 00:00 até hoje 23:59)
        workStatusMinTime.value = twoDaysAgo.getTime()
        const maxDate = new Date(today)
        maxDate.setHours(23, 59, 59, 999)
        workStatusMaxTime.value = maxDate.getTime()

        // Fazer 3 fetches em paralelo
        const [responseToday, responseYesterday, responseTwoDaysAgo] = await Promise.all([
          store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetWorkstatusHistory`, {
            id: myId,
            dt: today.toISOString()
          }),
          store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetWorkstatusHistory`, {
            id: myId,
            dt: yesterday.toISOString()
          }),
          store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetWorkstatusHistory`, {
            id: myId,
            dt: twoDaysAgo.toISOString()
          })
        ])

        // Combinar todos os dados
        const allData = []

        // Processar dados de anteontem
        if (responseTwoDaysAgo.data && responseTwoDaysAgo.data.data && Array.isArray(responseTwoDaysAgo.data.data)) {
          responseTwoDaysAgo.data.data.forEach(item => {
            allData.push({
              workStatus: item.workStatus,
              start: item.start,
              end: item.end
            })
          })
        }

        // Processar dados de ontem
        if (responseYesterday.data && responseYesterday.data.data && Array.isArray(responseYesterday.data.data)) {
          responseYesterday.data.data.forEach(item => {
            allData.push({
              workStatus: item.workStatus,
              start: item.start,
              end: item.end
            })
          })
        }

        // Processar dados de hoje
        if (responseToday.data && responseToday.data.data && Array.isArray(responseToday.data.data)) {
          responseToday.data.data.forEach(item => {
            allData.push({
              workStatus: item.workStatus,
              start: item.start,
              end: item.end
            })
          })
        }

        // Agrupar por status e ordenar por data/hora
        const statusMap = new Map()

        // Backend já envia start/end no timezone do usuário - não interpretar como UTC
        allData.forEach(item => {
          const startTime = new Date(item.start).getTime()
          const endTime = new Date(item.end).getTime()

          if (!statusMap.has(item.workStatus)) {
            statusMap.set(item.workStatus, [])
          }

          statusMap.get(item.workStatus).push({
            x: 'Status',
            y: [startTime, endTime],
            fillColor: resolveColor(item.workStatus)
          })
        })

        // Converter para array e ordenar cada grupo por hora de início
        statusMap.forEach((dataArray, workStatus) => {
          dataArray.sort((a, b) => a.y[0] - b.y[0])

          dataWorkStatus.value.push({
            name: workStatus,
            data: dataArray
          })
        })

        // Ordenar os status por hora de início do primeiro evento
        dataWorkStatus.value.sort((a, b) => {
          const aFirstTime = a.data[0]?.y[0] || 0
          const bFirstTime = b.data[0]?.y[0] || 0
          return aFirstTime - bFirstTime
        })

      } catch (error) {
        dataWorkStatus.value = []
      } finally {
        workStatusLoading.value = false
      }
    }

    // Função para carregar preferências do mapa do localStorage
    const loadMapPreferences = () => {
      const savedLayer = localStorage.getItem('assetSummaryMapLayer')

      if (isSatelliteBaseLayer(savedLayer)) {
        defaultLayer.value = 'satellite'
      } else {
        defaultLayer.value = 'map'
      }
    }

    // Função para salvar todas as preferências do mapa
    const saveAllMapPreferences = () => {
      if (instance?.proxy?.$refs?.summaryMap?.mapObject) {
        const map = instance.proxy.$refs.summaryMap.mapObject

        // Salvar camada base ativa
        map.eachLayer((layer) => {
          if (layer.options && layer.options.layerType === 'base' && map.hasLayer(layer)) {
            const layerName = layer.options.name || ''
            if (isMapBaseLayer(layerName)) {
              defaultLayer.value = 'map'
              localStorage.setItem('assetSummaryMapLayer', 'map')
            } else if (isSatelliteBaseLayer(layerName)) {
              defaultLayer.value = 'satellite'
              localStorage.setItem('assetSummaryMapLayer', 'satellite')
            }
          }
        })

        // Salvar estado dos overlays
        let labelsActive = false
        let roadsActive = false

        map.eachLayer((layer) => {
          if (layer.options && layer.options.layerType === 'overlay' && map.hasLayer(layer)) {
            const layerName = layer.options.name || ''
            if (isLabelsOverlay(layerName)) {
              labelsActive = true
            } else if (isRoadsOverlay(layerName)) {
              roadsActive = true
            }
          }
        })

        localStorage.setItem('assetSummaryShowLabels', labelsActive.toString())
        localStorage.setItem('assetSummaryShowRoads', roadsActive.toString())
      }
    }

    // Função para salvar preferências quando o mapa mudar
    const setupMapListeners = () => {
      const trySetup = (attempts = 0) => {
        if (instance?.proxy?.$refs?.summaryMap?.mapObject) {
          const map = instance.proxy.$refs.summaryMap.mapObject

          // Listener para mudanças de camada base
          map.on('baselayerchange', (e) => {
            const layerName = e.layer.options?.name || ''
            if (isMapBaseLayer(layerName)) {
              defaultLayer.value = 'map'
              localStorage.setItem('assetSummaryMapLayer', 'map')
            } else if (isSatelliteBaseLayer(layerName)) {
              defaultLayer.value = 'satellite'
              localStorage.setItem('assetSummaryMapLayer', 'satellite')
            }
          })

          // Listener para overlays adicionados
          map.on('overlayadd', (e) => {
            const layerName = e.layer.options?.name || ''
            if (isLabelsOverlay(layerName)) {
              localStorage.setItem('assetSummaryShowLabels', 'true')
            } else if (isRoadsOverlay(layerName)) {
              localStorage.setItem('assetSummaryShowRoads', 'true')
            }
          })

          // Listener para overlays removidos
          map.on('overlayremove', (e) => {
            const layerName = e.layer.options?.name || ''
            if (isLabelsOverlay(layerName)) {
              localStorage.setItem('assetSummaryShowLabels', 'false')
            } else if (isRoadsOverlay(layerName)) {
              localStorage.setItem('assetSummaryShowRoads', 'false')
            }
          })
        } else {
          if (attempts < 10) {
            setTimeout(() => trySetup(attempts + 1), 500)
          }
        }
      }

      trySetup()
    }

    // Não usar watch para atualizar center/zoom automaticamente
    // Isso será feito apenas via fitBounds quando os dados chegarem

    onMounted(() => {
      loadMapPreferences()

      fetchAsset()
      fetchAssetSummary()
      fetchTelemetrySummaryParameter()
      fetchAssetWorkstatusHistory()
      fetchFailuresData()
      fetchMaintenanceData()

      // Buscar parâmetros de telemetria e inicializar data do gráfico
      store.dispatch(`${ASSET_APP_STORE_MODULE_NAME}/fetchAssetTelemetryParameters`, {
        id: myId
      })
        .then(res => {
          telemetryParameters.value = res.data
          fetchChartLimits()
          // Inicializar com data de hoje (fetch só ao abrir a aba Gráfico diário)
          const today = new Date()
          const month = today.getMonth() + 1
          chartDate.value = `${today.getFullYear()}-${String(month).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        })
        .catch(() => { })
        .finally(() => {
          telemetryParamsLoading.value = false
        })

      // Configurar listeners após o mapa ser renderizado
      nextTick(() => {
        setupMapListeners()
      })
    })

    // Métodos para opções do asset
    const handleVirtualFence = () => {
      // Emitir evento para o componente pai ou navegar diretamente
      // Se necessário, pode ser adaptado para emitir evento ou abrir modal
      // Se houver um método no AssetPreview, pode ser chamado aqui
      // instance.proxy.$parent.$emit('event-criterion')
    }

    const handleBlock = () => {
      // Emitir evento para o componente pai ou abrir modal
      // Por enquanto, vamos usar SweetAlert para confirmar
      if (instance?.proxy?.$swal) {
        instance.proxy.$swal({
          title: instance.proxy.$t('Are you sure?'),
          text: instance.proxy.$t('Do you want to activate/deactivate the lock?'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: instance.proxy.$t('Yes'),
          cancelButtonText: instance.proxy.$t('No'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1'
          },
          buttonsStyling: false
        }).then((result) => {
          if (result.isConfirmed) {
            // Aqui você pode adicionar a lógica para ativar/desativar o bloqueio
            instance.proxy.$swal({
              icon: 'success',
              title: instance.proxy.$t('Success'),
              text: instance.proxy.$t('Lock status updated successfully'),
              customClass: {
                confirmButton: 'btn btn-primary'
              },
              buttonsStyling: false
            })
          }
        })
      }
    }

    const parameterIconMap = {
        'hod': 'MapIcon',
        'hor': 'ClockIcon',
        'hrb': 'ClockIcon',
        'rpm': 'RotateCwIcon',
        'cbt': 'DropletIcon',
        'tpa': 'ThermometerIcon',
        'cbc': 'DropletIcon',
        'pcb': 'DropletIcon',
        'ptb': 'ZapIcon',
        'cgm': 'ActivityIcon',
        'mta': 'ThermometerIcon',
        'mcc': 'DropletIcon',
        'mtq': 'ZapIcon',
        'tom': 'ThermometerIcon',
        'cvr': 'AlertCircleIcon',
        'tpt': 'ThermometerIcon',
        'mtt': 'ThermometerIcon',
        'ret': 'SettingsIcon',
        'mat': 'SettingsIcon',
        'toh': 'ThermometerIcon',
        'mth': 'ThermometerIcon',
        'prh': 'DropletIcon',
        'btf': 'AlertCircleIcon',
        'tbt': 'ZapIcon',
        'soc': 'BatteryIcon',
        'soh': 'ActivityIcon',
        'ttb': 'ZapIcon',
        'tpb': 'ThermometerIcon',
        'ibt': 'ZapIcon',
        'tec': 'BatteryChargingIcon',
        'ted': 'BatteryIcon',
        'sec': 'BatteryIcon',
        'crg': 'PackageIcon',
        'opc': 'SettingsIcon',
        'ccc': 'RepeatIcon',
        'tcg': 'PackageIcon',
        'tda': 'ThermometerIcon',
        'pda': 'InfoIcon',
        'err': 'AlertTriangleIcon',
        'acl': 'ZapIcon',
        'kmh': 'ActivityIcon',
        'bus': 'NavigationIcon',
        'acx': 'ActivityIcon',
        'acy': 'ActivityIcon',
        'acz': 'ActivityIcon',
        'cab': 'AlertCircleIcon',
        'cfb': 'AlertCircleIcon',
        'mkm': 'ActivityIcon',
        'ilt': 'TrendingUpIcon',
        'ilg': 'TrendingUpIcon',
        'vvt': 'WindIcon',
        'cpl': 'Maximize2Icon',
        'agl': 'CompassIcon',
        'ral': 'TargetIcon',
        'cag': 'PackageIcon',
        'cng': 'PackageIcon',
        'pcg': 'PercentIcon',
        'alg': 'ArrowUpIcon',
        'arg': 'RotateCwIcon',
        'icg': 'TrendingUpIcon'
    }

    const resolveParameterIcon = (parameterName) => {
        if (!parameterName) return 'InfoIcon'
        return parameterIconMap[parameterName.toLowerCase()] || 'InfoIcon'
    }



    const SYSTEM_CONFIG = {
      Vehicle: { labelKey: 'Vehicle', icon: 'TruckIcon' },
      Engine: { labelKey: 'Engine', icon: 'ZapIcon' },
      Transmission: { labelKey: 'Transmission', icon: 'SettingsIcon' },
      Hydraulic: { labelKey: 'Hydraulic', icon: 'DropletIcon' },
      Battery: { labelKey: 'Battery', icon: 'BatteryIcon' },
      Cargo: { labelKey: 'Cargo', icon: 'PackageIcon' },
      Tires: { labelKey: 'Tires', icon: 'CircleIcon' }
    }

    const getSystemIcon = (systemName) => {
       if (!systemName) return 'SettingsIcon'
       return SYSTEM_CONFIG[systemName]?.icon || 'SettingsIcon'
    }

    const getSystemLabel = (systemName) => {
       if (!systemName) return systemName
       return SYSTEM_CONFIG[systemName] ? $t(SYSTEM_CONFIG[systemName].labelKey) : systemName
    }

    const availableSystems = computed(() => {
        const systems = new Set()
        summaryParametersList.value.forEach(item => {
            if (item.systemTypeName && item.systemTypeName !== 'Tires') {
                systems.add(item.systemTypeName)
            }
        })
        
        const systemsList = Array.from(systems).map(sys => ({
            name: sys,
            icon: getSystemIcon(sys),
            label: getSystemLabel(sys)
        }))
        
        if (tiresData.value.length > 0) {
            systemsList.push({ name: 'Tires', icon: 'CircleIcon', label: $t('Tires') })
        }
        
        return systemsList
    })

    const hasAllPanelData = computed(() => availableSystems.value.length > 0)

    const getSystemData = (systemName) => {
        return summaryParametersList.value
            .filter(item => item.systemTypeName === systemName)
            .map(item => {
                const code = item.parameterName ? item.parameterName.toLowerCase() : ''
                const entry = getTelemetryEntry(code)
                
                let value = item.parameterValue
                let timestamp = item.parameterDate ? formatTelemetryTimestamp({ parameterDate: item.parameterDate }) : ''
                
                if (entry && entry.value !== undefined && entry.value !== null) {
                   value = entry.value
                   timestamp = formatTelemetryTimestamp(entry)
                }

                if (code === 'btf' && (value === false || value === 'false' || value === '0' || value === 0)) return null

                if ((code === 'acl' || code === 'acx' || code === 'acy' || code === 'acz') && !isNaN(Number(value))) {
                    value = Number(value).toFixed(2)
                } else if (code === 'cbc' && !isNaN(Number(value))) {
                    value = Number(value).toFixed(1)
                } else if (code === 'tcg' && !isNaN(Number(value))) {
                    value = Number(value).toFixed(2)
                } else if (code === 'opc') {
                    value = value == 1 ? $t('Charge') : value == 2 ? $t('Discharge') : value
                } else if (code === 'err' && value) {
                    value = $t('Error detected')
                } else if (code === 'btf') {
                    value = $t(value ? 'Yes' : 'No')
                }

                return {
                    label: item.parameterLabel || $t(code),
                    value: value,
                    unit: item.parameterUnit || '',
                    icon: resolveParameterIcon(code),
                    timestamp: timestamp
                }
            })
            .filter(item => item !== null && item.value !== null && item.value !== '' && item.value !== undefined)
    }

    const hasCrgParameter = computed(() => {
      const inSummary = summaryParametersList.value?.some(item => {
        const code = String(item?.parameterName || item?.code || item?.name || '').trim().toLowerCase()
        return code === 'crg'
      })
      return Boolean(inSummary || telemetrySnapshot.value?.crg)
    })

    const hasTcgParameter = computed(() => {
      const inSummary = summaryParametersList.value?.some(item => {
        const code = String(item?.parameterName || item?.code || item?.name || '').trim().toLowerCase()
        return code === 'tcg'
      })
      return Boolean(inSummary || telemetrySnapshot.value?.tcg)
    })

    const toAssetPreview = () => {
      router.push({ name: 'preview-asset', params: { id: myId } })
    }

    const toAssetRegister = () => {
      router.push({ name: 'register-asset', params: { id: myId } })
    }

    const goToAssetAlarms = () => {
      const info = assetInfo.value || {}
      const assetLabel = info.plate || info.chassisNumber || info.nickname || info.nickName || ''
      const query = { assetId: String(myId) }
      if (assetLabel) query.assetLabel = assetLabel

      router.push({
        name: 'customer-alarms',
        query,
      })
    }

    // Helper para obter código do pneu (formato: 01-09 para pneus 1-9, 0a para pneu 10)
    const getTireCode = (index) => {
      if (index < 10) {
        return `0${index}`
      }
      // Para o pneu 10, usa '0a' (hexadecimal)
      return '0a'
    }

    // Helper para obter dados dos pneus
    const getTireData = (index) => {
      const tireCode = getTireCode(index)
      const pressureCode = `p${tireCode}`
      const temperatureCode = `t${tireCode}`

      const pressureEntry = getTelemetryEntry(pressureCode)
      const temperatureEntry = getTelemetryEntry(temperatureCode)

      if (!pressureEntry && !temperatureEntry) return null

      return {
        index: index,
        pressure: pressureEntry?.value ?? null,
        temperature: temperatureEntry?.value ?? null,
        timestamp: formatTelemetryTimestamp(pressureEntry || temperatureEntry)
      }
    }

    const tiresData = computed(() => {
      const tireCount = getTelemetryValue('cpn', 0)
      const maxTires = Math.min(Number(tireCount) || 10, 10)
      const tires = []

      for (let i = 1; i <= maxTires; i++) {
        const tireData = getTireData(i)
        if (tireData && (tireData.pressure !== null || tireData.temperature !== null)) {
          tires.push(tireData)
        }
      }

      return tires
    })

    /**
     * Atualiza a tela com dados de telemetria em tempo real recebidos via SignalR.
     * Só aplica alterações quando o payload pertence ao ativo atualmente aberto (assetId).
     * Atualiza: painéis (telemetrias e datas), localização no mapa e status de trabalho.
     * @param {Object} content - Payload do SignalR (assetId, data?, deviceDateTime?, serverDateTime?, lat?, lng?, workStatus?)
     */
    const event_tlm = (content) => {
      if (!content || content.assetId !== myId) return

      const receivedAt = resolveTelemetryDate(content.serverDateTime ?? content.deviceDateTime) ?? null

      // 1) Atualizar todos os parâmetros de telemetria na aba Painéis (qualquer código exibido nos cards: hod, hor, hrb, tpa, rpm, cbt, etc.)
      // data pode ser null, vazio ou conter apenas alguns parâmetros; cada chave em data é aplicada ao snapshot e reflete nos painéis
      if (content.data && typeof content.data === 'object') {
        Object.entries(content.data).forEach(([code, value]) => {
          if (code && (value !== null && value !== undefined)) {
            updateTelemetryEntry(code, value, { receivedAt })
          }
        })

        // Sincronizar odômetro e horímetro nos cards de resumo do topo (hod/hor)
        const hodEntry = getTelemetryEntry('hod')
        const horEntry = getTelemetryEntry('hor')
        const nextOdometerHourmeter = { ...odometerHourmeter.value }
        if (hodEntry != null && hodEntry.value !== null && hodEntry.value !== undefined) {
          nextOdometerHourmeter.odometer = String(hodEntry.value)
          nextOdometerHourmeter.odometerTimestamp = formatTelemetryTimestamp(hodEntry)
        }
        if (horEntry != null && horEntry.value !== null && horEntry.value !== undefined) {
          nextOdometerHourmeter.hourmeter = String(horEntry.value)
          nextOdometerHourmeter.hourmeterTimestamp = formatTelemetryTimestamp(horEntry)
        }
        odometerHourmeter.value = nextOdometerHourmeter
      }

      // 2) Atualizar localização no mapa (apenas se lat/lng válidos)
      const lat = content.lat != null ? Number(content.lat) : null
      const lng = content.lng != null ? Number(content.lng) : null
      const hasValidCoords = typeof lat === 'number' && typeof lng === 'number' &&
        !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0

      if (hasValidCoords) {
        latLong.value = [lat, lng]
        const positionTime = content.serverDateTime ?? content.deviceDateTime
        lastPositionTime.value = positionTime ? formatDate(positionTime) : lastPositionTime.value
        assetSummary.value = {
          ...assetSummary.value,
          lastLat: lat,
          lastLng: lng,
          ...(positionTime && { lastPositionTime: positionTime })
        }
        nextTick(() => {
          setTimeout(updateMapPosition, 300)
        })
      }

      // 3) Atualizar status de trabalho atual (badge e ícone no mapa)
      if (content.workStatus != null && content.workStatus !== '') {
        assetSummary.value = {
          ...assetSummary.value,
          lastWorkStatus: content.workStatus
        }
      }
    }

    return {
      ASSET_APP_STORE_MODULE_NAME,
      myId,
      user,
      assetInfo,
      deviceModemIdentifier,
      assetSummary,
      telemetrySummaryLoading,
      telemetryCards,
      hasTelemetryCards,
      odometerHourmeter,
      dataWorkStatus,
      workStatusLoading,
      workStatusMinTime,
      workStatusMaxTime,
      workStatusPeriod,
      mainAssetTabIndex,
      telemetryParamsLoading,
      telemetryParameters,
      chartDate,
      chartLoading,
      chartLimitsMap,
      hasChartData,
      getChartLimit,
      flatpickrConfig,
      onChartDateChange,
      timeConnectionRpm,
      rpmHistory,
      timeConnectionEngine,
      engineHistory,
      timeConnectionTransmission,
      transmissionHistory,
      timeConnectionOil,
      oilHistory,
      timeConnectionCbc,
      cbcHistory,
      timeConnectionCbt,
      cbtHistory,
      timeConnectionPcb,
      pcbHistory,
      timeConnectionPom,
      pomHistory,
      timeConnectionPtb,
      ptbHistory,
      timeConnectionTbt,
      tbtHistory,
      timeConnectionTtb,
      ttbHistory,
      timeConnectionAcl,
      aclHistory,
      timeConnectionKmh,
      kmhHistory,
      timeConnectionCgm,
      cgmHistory,
      timeConnectionSoc,
      socHistory,
      timeConnectionTpb,
      tpbHistory,
      timeConnectionTom,
      tomHistory,
      timeConnectionTda,
      tdaHistory,
      timeConnectionPda,
      pdaHistory,
      timeConnectionPpa,
      ppaHistory,
      timeConnectionNom,
      nomHistory,
      timeConnectionDfc,
      dfcHistory,
      timeConnectionCra,
      craHistory,
      timeConnectionPff,
      pffHistory,
      timeConnectionPft,
      pftHistory,
      timeConnectionPfr,
      pfrHistory,
      timeConnectionPfm,
      pfmHistory,
      timeConnectionPdr,
      pdrHistory,
      timeConnectionTot,
      totHistory,
      timeConnectionTmt,
      tmtHistory,
      timeConnectionTc1,
      tc1History,
      timeConnectionTc2,
      tc2History,
      timeConnectionTc3,
      tc3History,
      timeConnectionTpf,
      tpfHistory,
      timeConnectionTpd,
      tpdHistory,
      timeConnectionAlt,
      altHistory,
      timeConnectionRcg,
      rcgHistory,
      timeConnectionBsc,
      bscHistory,
      timeConnectionVvt,
      vvtHistory,
      timeConnectionCpl,
      cplHistory,
      timeConnectionAgl,
      aglHistory,
      timeConnectionRal,
      ralHistory,
      timeConnectionCag,
      cagHistory,
      timeConnectionCng,
      cngHistory,
      timeConnectionPcg,
      pcgHistory,
      timeConnectionAlg,
      algHistory,
      timeConnectionArg,
      argHistory,
      timeConnectionIcg,
      icgHistory,
      hasNonZeroData,
      getTelemetryValue,
      tryFixDecimalNumbers,
      fetchAsset,
      fetchAssetSummary,
      fetchTelemetrySummaryParameter,
      fetchAssetWorkstatusHistory,
      resolveIcon,
      latLong,
      center,
      zoom,
      lastPositionTime,
      imageicon,
      defaultLayer,
      showLabels,
      showRoads,
      arcgisSatelliteUrl,
      arcgisLabelsUrl,
      arcgisRoadsUrl,
      dynamicSize,
      dynamicAnchor,
      dynamicTooltip,
      dynamicPopup,
      handleImageError,
      handleImageLoad,
      toGoogleMap,
      isFullscreen,
      toggleFullscreen,
      mapOptions,
      onMapReady,
      failuresData,
      maintenanceData,
      formatFailureDate,
      getFailureTypeLabel,
      getStatusVariant,
      handleVirtualFence,
      handleBlock,
      weatherData,
      weatherLoading,
      weatherError,
      weatherForecast,
      weatherLocation,
      weatherCurrent,
      fetchWeatherData,
      formatForecastDate,
      deviceId,
      hasBlock,
      summaryParametersList,
      hasCrgParameter,
      hasTcgParameter,
      tiresData,
      availableSystems,
      hasAllPanelData,
      getSystemData,
      event_tlm,
      toAssetPreview,
      toAssetRegister,
      goToAssetAlarms,
    }
  }
}

</script>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';

.map-card {
  position: relative;
  transition: all 0.3s ease;

  .summary-map {
    height: 500px;
    width: 100%;
    border-radius: 0.357rem;
    overflow: hidden;
  }

  .gap-2 {
    gap: 0.5rem;
  }
}

.fullscreen-map-card {
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  margin: 0 !important;
  border-radius: 0 !important;
  max-width: 100% !important;
  width: 100vw !important;
  height: 100vh !important;

  .summary-map {
    height: calc(100vh - 120px) !important;
    border-radius: 0 !important;
  }

  .card-body {
    min-height: calc(100vh - 120px);
    padding: 1rem;
  }
}


.someExtraClass {
  padding: 4px;
  border-radius: 50px;
  box-shadow: 5px 3px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  width: auto !important;
  height: auto !important;
}

.asset-on {
  background-color: #28c76f !important;
}

.asset-off {
  background-color: #82868b !important;
}

.asset-failure {
  background-color: #ef5258 !important;
}

.asset-noconnection {
  background-color: #f5f4f8 !important;
}

.asset-severe {
  background-color: #ff9d54 !important;
}

.asset-iddle {
  background-color: #00cfe8 !important;
}

.with-icon {
  width: 3rem;
}

@media (max-width: 768px) {
  .map-card {
    .summary-map {
      height: 400px;
    }
  }
}

.modern-tabs-wrapper {
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
}

.modern-tabs-nav {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 2px solid #e9ecef;
  padding: 0.75rem 1rem 0;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  .nav-item {
    margin-bottom: 0;
  }

  .nav-link {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem 0.5rem 0 0;
    color: #6c757d;
    font-weight: 500;
    font-size: 0.95rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: none;
    background: transparent;
    position: relative;
    margin-right: 0.25rem;
    white-space: nowrap;


    &.active {
      color: #fff;
      background: linear-gradient(135deg, #5e72e4 0%, #667eea 100%);
      font-weight: 600;
      box-shadow: 0 -2px 8px rgba(94, 114, 228, 0.15);

      &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #5e72e4 0%, #667eea 100%);
        border-radius: 2px 2px 0 0;
        z-index: 0;
      }

      .feather,
      svg.feather {
        color: #fff !important;
        stroke: #fff !important;
        fill: none !important;
      }
    }

    .feather,
    svg.feather {
      transition: all 0.3s ease;
      margin-right: 0.5rem;
      color: #6c757d;
      stroke: #6c757d;
      fill: none;
      position: relative;
      z-index: 1;
    }

    &:hover {
      color: #5e72e4;
      background: rgba(94, 114, 228, 0.08);
      transform: translateY(-2px);

      .feather,
      svg.feather {
        color: #5e72e4;
        stroke: #5e72e4;
      }
    }

    span {
      line-height: 1.2;
    }
  }
}

.modern-tabs-content {
  padding: 0.75rem;
  min-height: 200px;
  background: #fff;
  border-radius: 0 0 0.5rem 0.5rem;
}

.tab-content-wrapper {
  animation: fadeIn 0.3s ease-in-out;
  padding: 1.5rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsividade
@media (max-width: 768px) {
  .modern-tabs-nav {
    padding: 0.5rem 0.75rem 0;
    gap: 0.25rem;

    .nav-link {
      padding: 0.6rem 1rem;
      font-size: 0.875rem;

      .feather {
        margin-right: 0.375rem;
        size: 14;
      }
    }
  }

  .modern-tabs-content {
    padding: 0.75rem;
  }
}

@media (max-width: 576px) {
  .modern-tabs-nav {
    .nav-link {
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;

      span {
        display: none;
      }

      .feather {
        margin-right: 0;
      }
    }
  }

  .modern-tabs-content {
    padding: 0.5rem;
  }
}

.status-history-card {
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .card-body {
    padding: 1.5rem;
  }
}

.charts-container {
  width: 100%;
  min-width: 0;

  .chart-card {
    border-radius: 0.5rem;
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
    overflow: visible;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }

    .card-body {
      padding: 1.25rem;
      min-width: 0;
      overflow: visible;
    }
  }
}

/* Card Principal de Visão Geral do Ativo */
.asset-overview-card {
  border: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  .card-body {
    padding: 1.5rem;
  }
}

/* Header com Avatar e Título */
.asset-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;

  .avatar-container-small {
    position: relative;
    flex-shrink: 0;

    .asset-avatar-small {
      width: 110px;
      height: 110px;
      border-radius: 0.5rem;
      border: 3px solid #fff;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 0.5rem;
      }

      &.asset-status-on {
        border-color: rgba(40, 199, 111, 0.25);
      }

      &.asset-status-off {
        border-color: rgba(130, 134, 139, 0.25);
      }

      &.asset-status-failure {
        border-color: rgba(234, 84, 85, 0.25);
      }

      &.asset-status-noconnection {
        border-color: rgba(212, 212, 212, 0.25);
      }

      &.asset-status-severe {
        border-color: rgba(255, 159, 67, 0.25);
      }

      &.asset-status-iddle {
        border-color: rgba(0, 207, 232, 0.25);
      }

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      }
    }

    .status-indicator-small {
      position: absolute;
      bottom: -1px;
      right: -1px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      border: 2px solid #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
      z-index: 10;

      &.status-on {
        background-color: #28c76f;
      }

      &.status-off {
        background-color: #82868b;
      }

      &.status-failure {
        background-color: #ea5455;
      }

      &.status-noconnection {
        background-color: #d4d4d4;
      }

      &.status-severe {
        background-color: #ff9f43;
      }

      &.status-iddle {
        background-color: #00cfe8;
      }
    }
  }

  .asset-title-section {
    flex: 1;
    min-width: 0;

    .asset-model {
      font-size: 1.25rem;
      font-weight: 700;
      color: #5e5873;
      line-height: 1.3;

      .text-muted {
        font-size: 0.875rem;
        font-weight: 400;
      }
    }

    .status-badge-header {
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
      font-weight: 600;
      border: 1px solid rgba(0, 0, 0, 0.1);
      text-transform: capitalize;

      &.badge-light {
        background-color: #e9ecef !important;
        color: #495057 !important;
        border-color: #adb5bd !important;
      }
    }

    .asset-identifier {
      display: flex;
      align-items: center;
      font-size: 0.8125rem;

      strong {
        font-size: 0.9375rem;
        letter-spacing: 0.3px;
      }
    }
  }
}

/* Seção de Informações */
.info-section {
  padding: 1.25rem 1.5rem;
  border-right: 1px solid #e9ecef;

  .asset-main-info {
    .asset-details-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
      margin-bottom: 0.75rem;

      .detail-item {
        padding: 0.625rem 0.75rem;
        background: #f8f9fa;
        border-radius: 0.5rem;
        border: 1px solid #e9ecef;
        transition: all 0.2s ease;

        &:hover {
          background: #f1f3f5;
          border-color: #dee2e6;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .detail-label {
          font-size: 0.65rem;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
          display: flex;
          align-items: center;

          svg {
            width: 13px;
            height: 13px;
            margin-right: 0.375rem;
          }
        }

        .detail-value {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #5e5873;
          word-break: break-word;
          line-height: 1.3;

          .status-badge-enhanced {
            font-size: 0.75rem;
            padding: 0.4rem 0.75rem;
            font-weight: 600;
            border: 1px solid rgba(0, 0, 0, 0.15);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
            text-transform: capitalize;
            letter-spacing: 0.3px;

            // Melhorar contraste para variante light (noconnection)
            &.badge-light {
              background-color: #e9ecef !important;
              color: #495057 !important;
              border-color: #adb5bd !important;
            }

            // Garantir contraste para outras variantes
            &.badge-success {
              border-color: rgba(255, 255, 255, 0.3);
            }

            &.badge-danger {
              border-color: rgba(255, 255, 255, 0.3);
            }

            &.badge-warning {
              border-color: rgba(0, 0, 0, 0.1);
            }

            &.badge-info {
              border-color: rgba(255, 255, 255, 0.3);
            }

            &.badge-secondary {
              border-color: rgba(255, 255, 255, 0.3);
            }
          }
        }
      }
    }

    .asset-metrics {
      display: flex;
      gap: 1rem;
      padding: 0.75rem;
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 0.5rem;
      border: 1px solid #e9ecef;

      .metric-item {
        display: flex;
        align-items: flex-start;
        gap: 0.5rem;
        flex: 1;

        svg {
          margin-top: 0.125rem;
          width: 14px;
          height: 14px;
        }

        .metric-label {
          font-size: 0.65rem;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.125rem;
        }

        .metric-value {
          font-size: 1.0625rem;
          font-weight: 700;
          color: #5e5873;
          line-height: 1.2;

          small {
            font-size: 0.75rem;
            font-weight: 500;
            color: #6c757d;
            margin-left: 0.125rem;
          }
        }

        .metric-timestamp {
          font-size: 0.6875rem;
          color: #a8a8a8;
          margin-top: 0.25rem;
          display: block;
        }
      }
    }
  }
}

/* Seção de Alertas (Falhas e Manutenção) */
.alerts-section {
  padding: 1.25rem 1.5rem;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  height: 100%;

  .alerts-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0;
    height: 100%;
    min-height: 0;
  }

  .failures-section,
  .maintenance-section {
    padding: 0;
  }

  .alerts-divider {
    margin: 1rem 0;
    border-top: 1px solid #e9ecef;
    border-bottom: none;
  }
}

.failures-section {
  padding-left: 0;
  margin-bottom: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  .failures-title {
    font-size: 1rem;
    color: #ff9800;
    display: flex;
    align-items: center;

    svg {
      color: #ff9800;
    }
  }

  .failures-list {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
    overflow-y: auto;

    .failure-card {
      padding: 0.75rem;
      border-radius: 0.5rem;
      border: 1px solid;
      transition: all 0.2s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
      }

      &.failure-card-warning {
        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
        border-color: #ffc107;
        color: #856404;

        .failure-code-title {
          color: #856404;
        }

        .failure-date-label {
          color: #856404;
        }

        .failure-header-content {
          .failure-date-label {
            color: #856404;
          }
        }

        .failure-description {
          color: #856404;
        }
      }

      &.failure-card-danger {
        background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
        border-color: #dc3545;
        color: #721c24;

        .failure-code-title {
          color: #721c24;
        }

        .failure-date-label {
          color: #721c24;
        }

        .failure-header-content {
          .failure-date-label {
            color: #721c24;
          }
        }

        .failure-description {
          color: #721c24;
        }
      }

      .failure-card-header {
        margin-bottom: 0.5rem;

        .failure-header-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;

          .failure-code-title {
            font-size: 1.0625rem;
            font-weight: 700;
            font-family: 'Courier New', monospace;
            letter-spacing: 0.5px;
            flex-shrink: 0;
          }

          .failure-date-label {
            font-size: 0.875rem;
            font-weight: 500;
            opacity: 0.9;
            flex: 1;
            min-width: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .failure-type-badge {
            flex-shrink: 0;
            font-size: 0.8125rem;
            padding: 0.25rem 0.5rem;
            font-weight: 600;
            margin-left: auto;
          }
        }
      }

      .failure-card-body {
        .failure-description {
          font-size: 0.9375rem;
          line-height: 1.4;
          font-weight: 400;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: normal;
        }
      }
    }
  }
}

.maintenance-section {
  flex-shrink: 0;
  margin-bottom: 0;

  &.maintenance-top {
    margin-bottom: 0;
  }

  .maintenance-title {
    font-size: 1rem;
    color: #00cfe8;
    display: flex;
    align-items: center;

    svg {
      color: #00cfe8;
    }
  }

  .maintenance-content {
    margin-top: 0.5rem;

    .maintenance-info-compact {
      padding: 0.875rem;
      background: linear-gradient(135deg, rgba(0, 207, 232, 0.1) 0%, rgba(0, 207, 232, 0.05) 100%);
      border-radius: 0.5rem;
      border: 1.5px solid rgba(0, 207, 232, 0.2);
      line-height: 1.5;
      box-shadow: 0 2px 4px rgba(0, 207, 232, 0.08);
      transition: all 0.2s ease;

      &:hover {
        border-color: rgba(0, 207, 232, 0.3);
        box-shadow: 0 4px 8px rgba(0, 207, 232, 0.12);
      }

      .maintenance-header-row {
        display: flex;
        align-items: flex-start;
        gap: 0;
        margin-bottom: 0.5rem;

        .maintenance-info-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.375rem;

          .maintenance-plan-row,
          .maintenance-step-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            min-width: 0;

            .maintenance-label {
              font-size: 0.75rem;
              font-weight: 600;
              color: #00cfe8;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              flex-shrink: 0;
            }

            .maintenance-plan-name {
              font-size: 0.875rem;
              font-weight: 500;
              color: #5e5873;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              flex: 1;
              min-width: 0;
            }

            .maintenance-step-name {
              font-size: 0.9375rem;
              font-weight: 600;
              color: #2c2c2c;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              flex: 1;
              min-width: 0;
            }
          }
        }
      }

      .maintenance-metrics-row {
        display: flex;
        align-items: center;
        gap: 1.25rem;
        margin-top: 0.625rem;
        padding-top: 0.625rem;
        border-top: 1px solid rgba(0, 207, 232, 0.15);

        .maintenance-metric {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8125rem;

          svg {
            width: 10px;
            height: 10px;
            flex-shrink: 0;
          }

          small {
            font-size: 0.8125rem;
            line-height: 1.2;
          }

          strong {
            font-weight: 600;
          }
        }
      }
    }
  }

  .maintenance-empty {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 0.5rem;
    border: 1px dashed #dee2e6;
    margin-top: 0.5rem;

    .maintenance-empty-content {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;

      .maintenance-empty-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #e9ecef;
        border-radius: 50%;
        color: #6c757d;

        svg {
          width: 16px;
          height: 16px;
        }
      }

      .maintenance-empty-text {
        flex: 1;
        min-width: 0;

        .maintenance-empty-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #5e5873;
          margin-bottom: 0.25rem;
          line-height: 1.3;
        }

        .maintenance-empty-description {
          font-size: 0.75rem;
          color: #6c757d;
          line-height: 1.4;
        }
      }
    }
  }
}


/* Responsividade */
@media (max-width: 991.98px) {
  .info-section {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }

  .alerts-section {
    background: #fff;
  }

  .asset-main-info {
    .asset-details-grid {
      grid-template-columns: 1fr;
    }
  }
}

@media (max-width: 768px) {
  .asset-header {
    flex-direction: column;
    align-items: center;
    text-align: center;

    .avatar-container-small {
      .asset-avatar-small {
        width: 88px;
        height: 88px;
      }
    }

    .asset-title-section {
      .asset-model {
        font-size: 1.25rem;
      }
    }
  }

  .asset-main-info {
    .asset-metrics {
      flex-direction: column;
      gap: 1rem;
    }
  }

  .failures-section {
    .failures-counters {
      grid-template-columns: 1fr;
    }
  }
}

/* Estilos para as subtabs de sistemas */
.system-tabs-nav {
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 1.5rem;

  .nav-link {
    color: #6e6b7b;
    font-weight: 500;
    padding: 0.75rem 1.25rem;
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.3s ease;

    &:hover {
      color: #7367f0;
      background-color: rgba(115, 103, 240, 0.05);
    }

    &.active {
      color: #7367f0;
      background-color: transparent;
      border-bottom-color: #7367f0;
    }
  }
}

.system-panel-content {
  padding: 0.5rem 0;
}

.system-info-card-compact {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  margin-bottom: 0;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-color: #d0d2d6;
  }

  .card-body {
    padding: 0.75rem;
  }
}

.system-info-content-compact {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.system-info-icon-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%);
  color: #fff;
  flex-shrink: 0;
}

.system-info-details-compact {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.system-info-label-compact {
  font-size: 0.6875rem;
  color: #6e6b7b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.system-info-value-compact {
  font-size: 1.125rem;
  font-weight: 700;
  color: #2c2c2c;
  line-height: 1.2;

  .system-info-unit-compact {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6e6b7b;
    margin-left: 0.125rem;
  }
}

.system-info-timestamp-compact {
  display: flex;
  align-items: center;
  font-size: 0.625rem;
  color: #a8a8a8;
  margin-top: 0.125rem;
  line-height: 1;

  svg {
    color: #a8a8a8;
    flex-shrink: 0;
  }
}

/* Estilos para TPMS (Pneus) */
.tpms-container {
  padding: 0.75rem 0;
}

.tpms-grid-custom {
  display: grid;
  justify-items: center;
  align-items: start;
  max-width: 900px;
  margin: 0 auto;

  // Layout customizado para 10 pneus
  &.tpms-10 {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(3, auto);
    column-gap: 0.25rem; // gap pequeno padrão
    row-gap: 1rem;

    // Linha 1: pneu 1 e 2 centralizados com gap médio
    .tire-pos-1 {
      grid-column: 1 / 3;
      justify-self: end;
      margin-right: 1rem; // gap médio
    }

    .tire-pos-2 {
      grid-column: 3 / 5;
      justify-self: start;
    }

    // Linha 2: pneu 3, 4, 5, 6
    .tire-pos-3 {
      grid-column: 1;
      margin-right: 0.25rem; // gap pequeno
    }

    .tire-pos-4 {
      grid-column: 2;
      margin-left: 0.25rem; // gap pequeno (esquerda)
      margin-right: 1.5rem; // gap maior (direita)
    }

    .tire-pos-5 {
      grid-column: 3;
      margin-left: 1.5rem; // gap maior (esquerda)
      margin-right: 0.25rem; // gap pequeno (direita)
    }

    .tire-pos-6 {
      grid-column: 4;
      margin-left: 0.25rem; // gap pequeno
    }

    // Linha 3: pneu 7, 8, 9, 10
    .tire-pos-7 {
      grid-column: 1;
      margin-right: 0.25rem; // gap pequeno
    }

    .tire-pos-8 {
      grid-column: 2;
      margin-left: 0.25rem; // gap pequeno (esquerda)
      margin-right: 1.5rem; // gap maior (direita)
    }

    .tire-pos-9 {
      grid-column: 3;
      margin-left: 1.5rem; // gap maior (esquerda)
      margin-right: 0.25rem; // gap pequeno (direita)
    }

    .tire-pos-10 {
      grid-column: 4;
      margin-left: 0.25rem; // gap pequeno
    }
  }

  // Layout para outros números (fallback)
  &:not(.tpms-10) {
    gap: 0.5rem;

    &.tpms-4 {
      grid-template-columns: repeat(2, 1fr);
      max-width: 800px;
    }

    &.tpms-6 {
      grid-template-columns: repeat(3, 1fr);
      max-width: 1000px;
    }

    &.tpms-8 {
      grid-template-columns: repeat(4, 1fr);
      max-width: 1200px;
    }
  }
}

.tire-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  width: 100%;
  max-width: 180px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.12);
    transform: translateY(-2px);
  }
}

.tire-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.75rem;
}

.tire-circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #d0d2d6;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: inset 0 1px 4px rgba(0, 0, 0, 0.08);

  &::before {
    content: '';
    position: absolute;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, #fff 0%, transparent 70%);
    opacity: 0.5;
  }
}

.tire-inner {
  position: relative;
  z-index: 1;
  text-align: center;
}

.tire-label {
  font-size: 0.625rem;
  font-weight: 600;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tire-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tire-metric {
  text-align: center;
  padding: 0.5rem 0.625rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.tire-timestamp-compact {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  color: #a8a8a8;
  margin-top: 0.125rem;
  line-height: 1;
  padding-top: 0.25rem;
  border-top: 1px solid #f0f0f0;

  svg {
    color: #a8a8a8;
    flex-shrink: 0;
  }
}

.tire-metric-label {
  font-size: 0.625rem;
  color: #6e6b7b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 0.375rem;
  line-height: 1.2;
}

.tire-metric-value {
  font-size: 1rem;
  font-weight: 700;
  color: #2c2c2c;
  line-height: 1.2;
}

.tire-metric-unit {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6e6b7b;
  margin-left: 0.125rem;
}

/* Responsividade para TPMS */
@media (max-width: 992px) {
  .tpms-grid-custom {
    &.tpms-10 {
      grid-template-columns: repeat(4, 1fr) !important;

      .tire-pos-1,
      .tire-pos-2,
      .tire-pos-3,
      .tire-pos-4,
      .tire-pos-5,
      .tire-pos-6,
      .tire-pos-7,
      .tire-pos-8,
      .tire-pos-9,
      .tire-pos-10 {
        margin-left: 0.25rem !important;
        margin-right: 0.25rem !important;
      }

      .tire-pos-1 {
        grid-column: 1 / 3 !important;
        justify-self: center !important;
        margin-right: 0.5rem !important;
      }

      .tire-pos-2 {
        grid-column: 3 / 5 !important;
        justify-self: center !important;
        margin-left: 0.5rem !important;
      }
    }

    &:not(.tpms-10) {

      &.tpms-8,
      &.tpms-6 {
        grid-template-columns: repeat(3, 1fr);
      }

      &.tpms-4 {
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }
}

@media (max-width: 768px) {
  .tpms-grid-custom {
    grid-template-columns: repeat(2, 1fr) !important;

    &.tpms-10 {

      .tire-pos-1,
      .tire-pos-2,
      .tire-pos-3,
      .tire-pos-4,
      .tire-pos-5,
      .tire-pos-6,
      .tire-pos-7,
      .tire-pos-8,
      .tire-pos-9,
      .tire-pos-10 {
        grid-column: auto !important;
        margin-left: 0.25rem !important;
        margin-right: 0.25rem !important;
        justify-self: center !important;
      }
    }
  }

  .tire-card {
    max-width: 100%;
    padding: 0.875rem;
  }

  .tire-circle {
    width: 60px;
    height: 60px;
  }

  .tire-label {
    font-size: 0.5625rem;
  }

  .tire-metric-value {
    font-size: 0.9375rem;
  }

  .tire-metric-label {
    font-size: 0.5625rem;
  }

  .system-info-content-compact {
    gap: 0.5rem;
  }

  .system-info-icon-compact {
    width: 36px;
    height: 36px;
  }

  .system-info-icon-compact svg {
    width: 16px;
    height: 16px;
  }

  .system-info-value-compact {
    font-size: 1rem;
  }

  .system-info-label-compact {
    font-size: 0.625rem;
  }
}

@media (max-width: 576px) {
  .tpms-grid-custom {
    grid-template-columns: 1fr !important;
    gap: 0.5rem;

    &.tpms-10 {

      .tire-pos-1,
      .tire-pos-2,
      .tire-pos-3,
      .tire-pos-4,
      .tire-pos-5,
      .tire-pos-6,
      .tire-pos-7,
      .tire-pos-8,
      .tire-pos-9,
      .tire-pos-10 {
        grid-column: 1 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
      }
    }
  }

  .tire-card {
    max-width: 280px;
    margin: 0 auto;
  }
}

// Estilos para a interface de clima - Design compacto
.weather-container {
  .current-weather-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;

    .card-body {
      color: white;
    }

    h6 {
      color: white !important;
    }

    small {
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }

  .current-weather-main {
    .weather-icon-large {
      img {
        width: 48px;
        height: 48px;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
      }
    }

    .temperature-display {
      .temp-value {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1;
        color: white;
      }

      .temp-unit {
        font-size: 1rem;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.9);
        vertical-align: top;
        margin-left: 0.125rem;
      }
    }

    .weather-condition {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.95);
      font-weight: 500;
      margin-top: 0.25rem;
    }

    .weather-details {
      .weather-detail-item {
        display: flex;
        align-items: center;
        color: rgba(255, 255, 255, 0.95);
        font-size: 0.75rem;

        .detail-value {
          font-weight: 600;
          margin-left: 0.25rem;
        }

        svg {
          color: rgba(255, 255, 255, 0.9);
        }
      }
    }
  }

  .forecast-section {
    h6 {
      color: #5e5873;
      font-size: 0.875rem;
    }

    .forecast-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 0.5rem;

      @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
      }

      @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
      }

      @media (max-width: 576px) {
        grid-template-columns: 1fr;
      }
    }

    .forecast-card {
      border: 1px solid #e0e0e0;
      border-radius: 0.357rem;
      transition: all 0.2s ease;
      background: #fff;
      min-height: auto;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        border-color: #7367f0;
      }

      .forecast-date {
        color: #5e5873;
        font-size: 0.75rem;
        text-transform: capitalize;
        line-height: 1.2;
      }

      .forecast-icon {
        img {
          width: 32px;
          height: 32px;
        }
      }

      .forecast-condition {
        min-height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.65rem;
        line-height: 1.2;
      }

      .forecast-temps {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        gap: 0.25rem;

        .temp-max {
          color: #ea5455;
          font-weight: 600;
        }

        .temp-min {
          color: #6e6b7b;
        }
      }
    }
  }

  .weather-loading,
  .weather-error,
  .weather-empty {
    min-height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}

// Dark layout support
.dark-layout {
  .weather-container {
    .forecast-section {
      h6 {
        color: #b4b7bd;
      }

      .forecast-card {
        background: #283046;
        border-color: rgba(255, 255, 255, 0.1);

        &:hover {
          border-color: #7367f0;
          background: #2f3651;
        }

        .forecast-date {
          color: #b4b7bd;
        }

        .forecast-condition {
          color: #8b8d93;
        }

        .forecast-temps {
          .temp-min {
            color: #8b8d93;
          }
        }

        .forecast-details {
          border-color: rgba(255, 255, 255, 0.1);
          color: #8b8d93;
        }
      }
    }
  }
}

// Estilos para a interface de opções - Design compacto
.asset-options-container {
  padding: 0.75rem 0;

  .options-section {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }

    .section-header {
      padding-bottom: 0.5rem;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      margin-bottom: 0.75rem;

      h6 {
        color: #5e5873;
        font-size: 1.0625rem;
        font-weight: 600;
      }
    }

    .gutter-xs {
      margin-left: -0.25rem;
      margin-right: -0.25rem;

      >[class*="col"] {
        padding-left: 0.25rem;
        padding-right: 0.25rem;
      }
    }
  }

  .option-card {
    border: 1px solid #e0e0e0;
    border-radius: 0.357rem;
    transition: all 0.2s ease;
    cursor: pointer;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    min-height: auto;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border-color: #7367f0;
    }

    &.tool-card {
      background: linear-gradient(135deg, #f8f8f8 0%, #ffffff 100%);

      &:hover {
        background: linear-gradient(135deg, #f0f0f0 0%, #fafafa 100%);
      }
    }

    &.danger-card {
      border-color: #ea5455;

      &:hover {
        border-color: #e42728;
        box-shadow: 0 2px 8px rgba(234, 84, 85, 0.15);
      }
    }

    .option-icon-wrapper {
      width: 32px;
      height: 32px;
      border-radius: 0.357rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: all 0.2s ease;

      &.bg-primary-light {
        background: rgba(113, 102, 240, 0.1);
      }

      &.bg-info-light {
        background: rgba(0, 207, 232, 0.1);
      }

      &.bg-success-light {
        background: rgba(40, 199, 111, 0.1);
      }

      &.bg-warning-light {
        background: rgba(255, 159, 67, 0.1);
      }

      &.bg-secondary-light {
        background: rgba(168, 170, 174, 0.1);
      }

      &.bg-gradient-primary {
        background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
      }

      &.bg-gradient-danger {
        background: linear-gradient(135deg, #ea5455 0%, #f08182 100%);
      }
    }

    .card-body {
      min-height: 48px;

      .option-title {
        color: #5e5873;
        font-weight: 600;
        font-size: 0.9375rem;
        line-height: 1.2;
      }

      .option-subtitle {
        color: #6e6b7b;
        font-size: 0.8125rem;
        line-height: 1.2;
        display: block;
        margin-top: 0.125rem;
      }

      .option-btn {
        border-radius: 0.25rem;
        padding: 0.25rem 0.5rem;
        min-width: 28px;
        height: 28px;
        transition: all 0.2s ease;

        &:hover {
          transform: translateX(1px);
        }
      }
    }
  }
}

// Dark layout support
.dark-layout {
  .asset-options-container {
    .options-section {
      .section-header {
        border-bottom-color: rgba(255, 255, 255, 0.1);

        h6 {
          color: #b4b7bd;
        }
      }
    }

    .option-card {
      background: #283046;
      border-color: rgba(255, 255, 255, 0.1);

      &:hover {
        border-color: #7367f0;
        background: #2f3651;
      }

      &.tool-card {
        background: linear-gradient(135deg, #283046 0%, #2f3651 100%);

        &:hover {
          background: linear-gradient(135deg, #2f3651 0%, #36405a 100%);
        }
      }

      .card-body {
        .option-title {
          color: #b4b7bd;
        }

        .option-subtitle {
          color: #8b8d93;
        }
      }
    }
  }
}
</style>
